package com.upcominds.expedite.web

import androidx.compose.runtime.*
import com.upcominds.expedite.di.getKoin
import com.upcominds.expedite.dto.*
import com.upcominds.expedite.features.dashboard.*
import com.upcominds.expedite.model.*
import com.upcominds.expedite.util.parseBackendInstantOrNull
import kotlinx.browser.window
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.random.Random

object DashboardStyles : StyleSheet() {
    val primaryColor = Color("#6200EE")
    val surfaceColor = Color("#FFFFFF")
    val backgroundColor = Color("#F0F2F5")
    val errorColor = Color("#B00020")
    val dividerColor = Color("#E0E0E0")

    val container by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        height(100.vh)
        property("font-family", "Roboto, Helvetica, Arial, sans-serif")
        backgroundColor(backgroundColor)
    }

    val sidebar by style {
        width(250.px)
        backgroundColor(surfaceColor)
        padding(24.px)
        property("border-right", "1px solid #e0e0e0")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        property("box-shadow", "2px 0 5px rgba(0,0,0,0.05)")
    }

    val sidebarTitle by style {
        color(primaryColor)
        fontWeight("bold")
        fontSize(22.px)
        margin(0.px)
    }

    val sidebarBrand by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(12.px)
        marginBottom(30.px)
    }

    val sidebarLogo by style {
        width(38.px)
        height(38.px)
        borderRadius(10.px)
        property("object-fit", "cover")
    }

    val sidebarItem by style {
        marginTop(10.px)
        marginBottom(10.px)
        cursor("pointer")
        color(Color("#333"))
        fontSize(16.px)
        padding(10.px)
        borderRadius(4.px)
        property("transition", "background-color 0.2s")

        hover {
            backgroundColor(Color("#f5f5f5"))
        }
    }

    val sidebarItemActive by style {
        backgroundColor(Color("#ede7f6")) // Light purple
        color(primaryColor)
        fontWeight("bold")
    }

    val contentArea by style {
        flex(1)
        minWidth(0.px)
        property("min-height", "0")
        padding(40.px)
        overflowY("auto")
    }

    val card by style {
        minWidth(0.px)
        backgroundColor(surfaceColor)
        borderRadius(8.px)
        padding(24.px)
        marginBottom(24.px)
        property("box-shadow", "0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)")
    }

    val button by style {
        backgroundColor(primaryColor)
        color(Color.white)
        border(0.px)
        borderRadius(4.px)
        padding(10.px, 24.px)
        fontSize(14.px)
        fontWeight("500")
        cursor("pointer")
        property("text-transform", "uppercase")
        property("box-shadow", "0 3px 1px -2px rgba(0,0,0,0.2), 0 2px 2px 0 rgba(0,0,0,0.14), 0 1px 5px 0 rgba(0,0,0,0.12)")
        property("transition", "background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms")
        letterSpacing(1.px)

        hover {
            backgroundColor(Color("#5000C0"))
            property("box-shadow", "0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)")
        }

        active {
            backgroundColor(Color("#3700b3"))
        }
    }

    val secondaryButton by style {
        backgroundColor(Color.transparent)
        color(primaryColor)
        border(1.px, LineStyle.Solid, primaryColor)
        borderRadius(4.px)
        padding(9.px, 23.px) // 1px less for border
        fontSize(14.px)
        fontWeight("500")
        cursor("pointer")
        property("text-transform", "uppercase")
        property("transition", "background-color 0.2s")

        hover {
            backgroundColor(Color("#ede7f6"))
        }
    }

    val deleteButton by style {
        backgroundColor(Color.transparent)
        color(errorColor)
        border(1.px, LineStyle.Solid, errorColor)
        borderRadius(4.px)
        padding(4.px, 8.px)
        fontSize(12.px)
        cursor("pointer")
        property("transition", "background-color 0.2s, color 0.2s")

        hover {
            backgroundColor(errorColor)
            color(Color.white)
        }
    }

    val errorBanner by style {
        backgroundColor(Color("#ffcdd2"))
        color(Color("#b71c1c"))
        padding(12.px, 16.px)
        borderRadius(4.px)
        marginBottom(20.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        fontWeight("500")
    }

    val textInput by style {
        width(100.percent)
        minWidth(0.px)
        padding(12.px, 14.px)
        borderRadius(4.px)
        border(1.px, LineStyle.Solid, Color("#c4c4c4"))
        marginBottom(16.px)
        fontSize(16.px)
        property("box-sizing", "border-box")
        property("transition", "border-color 0.2s")

        focus {
            property("border-color", "#6200EE")
            property("outline", "none")
            border(2.px, LineStyle.Solid, primaryColor)
            padding(11.px, 13.px) // adjusting for border width
        }
    }

    val selectInput by style {
        width(100.percent)
        padding(12.px, 14.px)
        borderRadius(4.px)
        border(1.px, LineStyle.Solid, Color("#c4c4c4"))
        marginBottom(16.px)
        fontSize(16.px)
        backgroundColor(surfaceColor)
        property("box-sizing", "border-box")
    }

    val dialogOverlay by style {
        position(Position.Fixed)
        top(0.px); left(0.px); right(0.px); bottom(0.px)
        backgroundColor(Color("rgba(0,0,0,0.6)"))
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        property("z-index", 1000)
        property("backdrop-filter", "blur(5px)")
    }

    val dialogContent by style {
        backgroundColor(surfaceColor)
        borderRadius(12.px)
        width(550.px)
        maxWidth(95.percent)
        maxHeight(90.vh)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        property("box-shadow", "0 20px 40px rgba(0,0,0,0.3)")
        overflow("hidden")
    }

    val dialogHeader by style {
        padding(20.px, 24.px)
        property("border-bottom", "1px solid #E0E0E0")
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
    }

    val dialogTitle by style {
        margin(0.px)
        fontSize(20.px)
        fontWeight("600")
        color(Color("#202124"))
    }

    val dialogBody by style {
        padding(24.px)
        overflowY("auto")
        flex(1)
    }

    val dialogFooter by style {
        padding(16.px, 24.px)
        property("border-top", "1px solid #E0E0E0")
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        gap(12.px)
        backgroundColor(Color("#F8F9FA"))
    }

    val formGroup by style {
        marginBottom(20.px)
    }

    val formLabel by style {
        display(DisplayStyle.Block)
        marginBottom(8.px)
        fontSize(14.px)
        fontWeight("500")
        color(Color("#5f6368"))
    }

    val statsGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 180px), 1fr))")
        gap(16.px)
        marginBottom(24.px)
    }

    val statCard by style {
        backgroundColor(surfaceColor)
        borderRadius(8.px)
        padding(18.px)
        property("box-shadow", "0 1px 3px rgba(0,0,0,0.08)")
    }

    val mutedText by style {
        color(Color("#6b7280"))
        fontSize(13.px)
    }

    val logHeader by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.FlexStart)
        gap(16.px)
        marginBottom(16.px)
    }

    val logBadges by style {
        display(DisplayStyle.Flex)
        flexWrap(FlexWrap.Wrap)
        gap(8.px)
        marginBottom(10.px)
    }

    val badge by style {
        display(DisplayStyle.InlineBlock)
        padding(4.px, 10.px)
        borderRadius(999.px)
        fontSize(12.px)
        fontWeight("600")
    }

    val badgeError by style {
        backgroundColor(Color("#fde7e9"))
        color(Color("#9f1239"))
    }

    val badgeWarn by style {
        backgroundColor(Color("#fff4db"))
        color(Color("#92400e"))
    }

    val badgeInfo by style {
        backgroundColor(Color("#e8f1ff"))
        color(Color("#1d4ed8"))
    }

    val badgeNeutral by style {
        backgroundColor(Color("#eef2f7"))
        color(Color("#374151"))
    }

    val logMessage by style {
        fontSize(16.px)
        fontWeight("500")
        color(Color("#111827"))
        property("line-height", "1.5")
        marginBottom(16.px)
        property("white-space", "pre-wrap")
        property("word-break", "break-word")
    }

    val logMetaGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 180px), 1fr))")
        gap(12.px)
        marginBottom(16.px)
    }

    val stackTrace by style {
        backgroundColor(Color("#111827"))
        color(Color("#f9fafb"))
        borderRadius(8.px)
        padding(16.px)
        fontSize(12.px)
        property("line-height", "1.5")
        overflowX("auto")
        maxHeight(260.px)
        overflowY("auto")
        property("white-space", "pre-wrap")
        property("word-break", "break-word")
    }

    val paginationBar by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        alignItems(AlignItems.Center)
        gap(16.px)
        marginTop(24.px)
    }
}

// Simple navigation state for the Dashboard
sealed class DashboardRoute {
    object Communities : DashboardRoute()
    object Competitions : DashboardRoute()
    object Questionnaires : DashboardRoute()
    object Puzzles : DashboardRoute()
    object Rewards : DashboardRoute()
    object MobileLogs : DashboardRoute()
    data class CompetitionDetail(val competitionId: String) : DashboardRoute()
    data class QuestionnaireDetail(val questionnaireId: String) : DashboardRoute()
    data class PuzzleDetail(val puzzleId: String) : DashboardRoute()
    data class CommunityDetail(val community: Community) : DashboardRoute()
    data class ChallengeDetail(val challenge: ChallengeDTO, val community: Community) : DashboardRoute()
    data class QuizDetail(val quiz: Quiz, val challenge: ChallengeDTO, val community: Community) : DashboardRoute()
}

@Composable
fun WebDashboardScreen(
    viewModel: DashboardViewModel = getKoin().get(),
    onLogout: () -> Unit
) {
    Style(DashboardStyles)
    val uiState by viewModel.uiState.collectAsStateWeb()
    val isPlatformAdmin = uiState.adminContext?.platformAdmin == true
    var route by remember { mutableStateOf<DashboardRoute>(DashboardRoute.Communities) }
    var compactLayout by remember { mutableStateOf(window.innerWidth <= 900) }
    var navigationOpen by remember { mutableStateOf(false) }
    fun closeNavigation() {
        navigationOpen = false
        (document.getElementById("admin-menu-toggle") as? HTMLElement)?.focus()
    }
    DisposableEffect(Unit) {
        val resizeListener: (Event) -> Unit = {
            compactLayout = window.innerWidth <= 900
            if (!compactLayout) navigationOpen = false
        }
        window.addEventListener("resize", resizeListener)
        onDispose { window.removeEventListener("resize", resizeListener) }
    }
    if (compactLayout && navigationOpen) {
        AdminFocusScope("admin-navigation", ::closeNavigation)
    }
    val sectionTitle = when (route) {
        is DashboardRoute.Competitions, is DashboardRoute.CompetitionDetail -> "Community Competitions"
        is DashboardRoute.Questionnaires, is DashboardRoute.QuestionnaireDetail -> "Questionnaires"
        is DashboardRoute.Puzzles, is DashboardRoute.PuzzleDetail -> "Puzzles"
        is DashboardRoute.Rewards -> "Rewards"
        is DashboardRoute.MobileLogs -> "Mobile Logs"
        else -> "Communities"
    }
    var showCreateCommunity by remember { mutableStateOf(false) }
    var showCreateCompetition by remember { mutableStateOf(false) }
    var editingCompetition by remember { mutableStateOf<CommunityCompetitionDTO?>(null) }
    var reschedulingCompetition by remember { mutableStateOf<CommunityCompetitionDTO?>(null) }
    var showCreateCompetitionChallenge by remember { mutableStateOf(false) }
    var editingCompetitionChallenge by remember { mutableStateOf<ChallengeDTO?>(null) }
    var showEditCommunity by remember { mutableStateOf(false) }
    var showCreateChallenge by remember { mutableStateOf(false) }
    var showEditChallenge by remember { mutableStateOf(false) }
    var showCreateQuiz by remember { mutableStateOf(false) }
    var showCreateQuestionnaire by remember { mutableStateOf(false) }
    var showEditQuestionnaire by remember { mutableStateOf(false) }
    var showCreatePuzzle by remember { mutableStateOf(false) }
    var showEditPuzzle by remember { mutableStateOf(false) }
    var showAddQuestion by remember { mutableStateOf(false) }
    var showAddPuzzleQuestion by remember { mutableStateOf(false) }
    var showCreateNews by remember { mutableStateOf(false) }
    var showCreateSponsor by remember { mutableStateOf(false) }
    var editingSponsor by remember { mutableStateOf<SponsorDTO?>(null) }
    var showCreateReward by remember { mutableStateOf(false) }
    var editingReward by remember { mutableStateOf<RewardDTO?>(null) }
    var editingNews by remember { mutableStateOf<CommunityNewsDTO?>(null) }
    val expandedNewsIds = remember { mutableStateMapOf<String, Boolean>() }
    val newsSurveyPageIndex = remember { mutableStateMapOf<String, Int>() }

    if (showCreateCommunity) {
        CommunityDialog(
            title = "Create Community",
            confirmText = "Create",
            onDismiss = { showCreateCommunity = false },
            onConfirm = { newCommunity ->
                viewModel.createCommunity(newCommunity)
                showCreateCommunity = false
            }
        )
    }

    if (showCreateCompetition) {
        CommunityCompetitionDialog(
            communities = uiState.communities,
            onDismiss = { showCreateCompetition = false },
            onConfirm = { request, communityIds ->
                viewModel.createCompetition(request, communityIds)
                showCreateCompetition = false
            }
        )
    }

    editingCompetition?.let { competition ->
        CommunityCompetitionDialog(
            communities = uiState.communities,
            existingCompetition = competition,
            onDismiss = { editingCompetition = null },
            onConfirm = { request, communityIds ->
                viewModel.updateCompetition(competition.id, request, communityIds)
                editingCompetition = null
            }
        )
    }

    reschedulingCompetition?.let { competition ->
        CompetitionScheduleDialog(
            competition = competition,
            onDismiss = { reschedulingCompetition = null },
            onConfirm = { request ->
                viewModel.updateCompetitionSchedule(competition.id, request)
                reschedulingCompetition = null
            }
        )
    }

    if (showCreateCompetitionChallenge && route is DashboardRoute.CompetitionDetail) {
        val competitionId = (route as DashboardRoute.CompetitionDetail).competitionId
        val competition = uiState.selectedCompetition?.takeIf { it.id == competitionId }
            ?: uiState.competitions.firstOrNull { it.id == competitionId }
        if (competition != null) {
            ChallengeDialog(
                title = "Create Shared Challenge",
                confirmText = "Create",
                uiState = uiState,
                viewModel = viewModel,
                useDateTimeDeadline = true,
                minimumDeadline = toDateTimeInputValue(competition.startsAt),
                maximumDeadline = toDateTimeInputValue(competition.endsAt),
                defaultDeadline = toDateTimeInputValue(competition.endsAt),
                onDismiss = { showCreateCompetitionChallenge = false },
                onConfirm = { challenge ->
                    viewModel.createCompetitionChallenge(competitionId, challenge)
                    showCreateCompetitionChallenge = false
                }
            )
        }
    }

    editingCompetitionChallenge?.let { challenge ->
        val competitionId = (route as? DashboardRoute.CompetitionDetail)?.competitionId
        if (competitionId != null) {
            ChallengeDialog(
                title = "Edit Shared Challenge",
                confirmText = "Update",
                existingChallengeDTO = challenge,
                uiState = uiState,
                viewModel = viewModel,
                useDateTimeDeadline = true,
                minimumDeadline = uiState.selectedCompetition?.startsAt?.let(::toDateTimeInputValue),
                maximumDeadline = uiState.selectedCompetition?.endsAt?.let(::toDateTimeInputValue),
                onDismiss = { editingCompetitionChallenge = null },
                onConfirm = { updatedChallenge ->
                    viewModel.updateCompetitionChallenge(competitionId, updatedChallenge)
                    editingCompetitionChallenge = null
                }
            )
        }
    }

    if (showEditCommunity && route is DashboardRoute.CommunityDetail) {
        val currentCommunity = (route as DashboardRoute.CommunityDetail).community
        CommunityDialog(
            title = "Edit Community",
            confirmText = "Update",
            existingCommunity = currentCommunity,
            onDismiss = { showEditCommunity = false },
            onConfirm = { updatedCommunity ->
                viewModel.updateCommunity(updatedCommunity)
                showEditCommunity = false
                route = DashboardRoute.CommunityDetail(updatedCommunity)
            }
        )
    }

    if (showCreateChallenge && route is DashboardRoute.CommunityDetail) {
        val currentCommunity = (route as DashboardRoute.CommunityDetail).community
        ChallengeDialog(
            title = "Create Challenge",
            confirmText = "Create",
            uiState = uiState,
            viewModel = viewModel,
            onDismiss = { showCreateChallenge = false },
            onConfirm = { newChallenge ->
                viewModel.createChallenge(newChallenge, currentCommunity.id)
                showCreateChallenge = false
            }
        )
    }

    if (showEditChallenge && route is DashboardRoute.ChallengeDetail) {
        val challengeDetail = route as DashboardRoute.ChallengeDetail
        val currentChallenge = challengeDetail.challenge
        val currentCommunity = challengeDetail.community

        ChallengeDialog(
            title = "Edit Challenge",
            confirmText = "Update",
            existingChallengeDTO = currentChallenge,
            uiState = uiState,
            viewModel = viewModel,
            onDismiss = { showEditChallenge = false },
            onConfirm = { updatedChallenge ->
                viewModel.updateChallenge(updatedChallenge, currentCommunity.id)
                showEditChallenge = false
            }
        )
    }

    if (showCreateQuiz && route is DashboardRoute.ChallengeDetail) {
        val currentChallenge = (route as DashboardRoute.ChallengeDetail).challenge
        CreateQuizDialog(
            challengeId = currentChallenge.id,
            onDismiss = { showCreateQuiz = false },
            onCreate = { newQuiz ->
                viewModel.createQuiz(newQuiz, currentChallenge.id)
                showCreateQuiz = false
            }
        )
    }

    if (showCreateNews && route is DashboardRoute.CommunityDetail) {
        val currentCommunity = (route as DashboardRoute.CommunityDetail).community
        CreateNewsDialog(
            onDismiss = { showCreateNews = false },
            onCreate = { title, text, imageUrl, surveys ->
                viewModel.createCommunityNews(
                    communityId = currentCommunity.id,
                    title = title,
                    text = text,
                    imageUrl = imageUrl,
                    surveys = surveys
                )
                showCreateNews = false
            }
        )
    }

    if (editingNews != null && route is DashboardRoute.CommunityDetail) {
        val currentCommunity = (route as DashboardRoute.CommunityDetail).community
        val currentNews = editingNews!!
        CreateNewsDialog(
            initialTitle = currentNews.title,
            initialText = currentNews.text,
            initialImageUrl = currentNews.imageUrl ?: "",
            initialSurveys = (currentNews.surveys.ifEmpty {
                currentNews.survey?.let { listOf(it) } ?: emptyList()
            }).map { survey ->
                NewsSurveyDraft(
                    question = survey.question,
                    optionsText = survey.options.joinToString("\n") { it.text }
                )
            },
            isEdit = true,
            onDismiss = { editingNews = null },
            onCreate = { title, text, imageUrl, surveys ->
                viewModel.updateCommunityNews(
                    communityId = currentCommunity.id,
                    newsId = currentNews.id,
                    title = title,
                    text = text,
                    imageUrl = imageUrl,
                    surveys = surveys
                )
                editingNews = null
            }
        )
    }

    if (showCreateQuestionnaire) {
        CreateQuestionnaireDialog(
            onDismiss = { showCreateQuestionnaire = false },
            onCreate = { name, description, infoUrl ->
                viewModel.createQuestionnaire(name, description, infoUrl)
                showCreateQuestionnaire = false
            }
        )
    }

    if (showEditQuestionnaire && uiState.selectedQuestionnaire != null) {
        val q = uiState.selectedQuestionnaire!!
        CreateQuestionnaireDialog(
            initialName = q.name,
            initialDescription = q.description ?: "",
            initialInfoUrl = q.infoUrl ?: "",
            isEdit = true,
            onDismiss = { showEditQuestionnaire = false },
            onCreate = { name, description, infoUrl ->
                viewModel.updateQuestionnaire(q.id, name, description, infoUrl)
                showEditQuestionnaire = false
            }
        )
    }

    if (showCreatePuzzle) {
        CreatePuzzleDialog(
            onDismiss = { showCreatePuzzle = false },
            onCreate = { name, description, numberOfQuestions ->
                viewModel.createPuzzle(name, description, numberOfQuestions)
                showCreatePuzzle = false
            }
        )
    }

    if (showEditPuzzle && uiState.selectedPuzzle != null) {
        val p = uiState.selectedPuzzle!!
        CreatePuzzleDialog(
            initialName = p.name,
            initialDescription = p.description ?: "",
            initialNumberOfQuestions = p.numberOfQuestions.toString(),
            isEdit = true,
            onDismiss = { showEditPuzzle = false },
            onCreate = { name, description, numberOfQuestions ->
                viewModel.updatePuzzle(p.id, name, description, numberOfQuestions)
                showEditPuzzle = false
            }
        )
    }

    if (showCreateSponsor) {
        SponsorDialog(
            title = "Create Sponsor",
            confirmText = "Create",
            onDismiss = { showCreateSponsor = false },
            onConfirm = {
                viewModel.createSponsor(it)
                showCreateSponsor = false
            }
        )
    }

    editingSponsor?.let { sponsor ->
        SponsorDialog(
            title = "Edit Sponsor",
            confirmText = "Update",
            existingSponsor = sponsor,
            onDismiss = { editingSponsor = null },
            onConfirm = {
                viewModel.updateSponsor(sponsor.id, it)
                editingSponsor = null
            }
        )
    }

    if (showCreateReward) {
        RewardDialog(
            title = "Create Reward",
            confirmText = "Create",
            sponsors = uiState.sponsors,
            onDismiss = { showCreateReward = false },
            onConfirm = { sponsorId, request ->
                viewModel.createReward(sponsorId, request)
                showCreateReward = false
            }
        )
    }

    editingReward?.let { reward ->
        RewardDialog(
            title = "Edit Reward",
            confirmText = "Update",
            existingReward = reward,
            sponsors = uiState.sponsors,
            onDismiss = { editingReward = null },
            onConfirm = { _, request ->
                viewModel.updateReward(reward.id, request)
                editingReward = null
            }
        )
    }

    Div({
        classes(DashboardStyles.container, "admin-shell")
    }) {
        if (compactLayout) {
            Div({ classes("admin-mobile-header") }) {
                Button({
                    id("admin-menu-toggle"); classes("admin-menu-toggle")
                    attr("aria-label", "Open navigation")
                    attr("aria-controls", "admin-navigation")
                    attr("aria-expanded", navigationOpen.toString())
                    onClick { navigationOpen = !navigationOpen }
                }) { Text("☰") }
                Div({ classes("admin-mobile-heading") }) {
                    B { Text("Expedite Admin") }
                    Span { Text(sectionTitle) }
                }
            }
        }
        if (compactLayout && navigationOpen) {
            Button({
                classes("admin-menu-scrim")
                attr("aria-label", "Dismiss navigation")
                attr("tabindex", "-1")
                onClick { closeNavigation() }
            }) {}
        }
        // Sidebar / Navigation
        if (!compactLayout || navigationOpen) {
            Div({
                classes(DashboardStyles.sidebar, "admin-sidebar")
                id("admin-navigation"); attr("role", "navigation"); attr("aria-label", "Admin navigation")
            }) {
                Div({ classes(DashboardStyles.sidebarBrand) }) {
                    Img(
                        src = "favicon.png",
                        attrs = {
                            classes(DashboardStyles.sidebarLogo)
                        }
                    )
                    H3({ classes(DashboardStyles.sidebarTitle) }) { Text("Expedite") }
                    if (compactLayout) Button({
                        id("admin-menu-close"); classes("admin-menu-close")
                        attr("aria-label", "Close navigation")
                        onClick { closeNavigation() }
                    }) { Text("×") }
                }

                Button({
                    classes(DashboardStyles.sidebarItem, "admin-nav-item")
                    if (sectionTitle == "Communities") {
                        classes(DashboardStyles.sidebarItemActive)
                        attr("aria-current", "true")
                    }
                    onClick { route = DashboardRoute.Communities; closeNavigation() }
                }) {
                    Text("Communities")
                }

                if (isPlatformAdmin) {
                    Button({
                        classes(DashboardStyles.sidebarItem, "admin-nav-item")
                        if (route is DashboardRoute.Competitions || route is DashboardRoute.CompetitionDetail) {
                            classes(DashboardStyles.sidebarItemActive)
                            attr("aria-current", "true")
                        }
                        onClick {
                            viewModel.loadCompetitions()
                            route = DashboardRoute.Competitions
                            closeNavigation()
                        }
                    }) { Text("Community Competitions") }

                    Button({
                        classes(DashboardStyles.sidebarItem, "admin-nav-item")
                        if (sectionTitle == "Questionnaires") { classes(DashboardStyles.sidebarItemActive); attr("aria-current", "true") }
                        onClick {
                            viewModel.loadQuestionnaires()
                            route = DashboardRoute.Questionnaires
                            closeNavigation()
                        }
                    }) { Text("Questionnaires") }

                    Button({
                        classes(DashboardStyles.sidebarItem, "admin-nav-item")
                        if (sectionTitle == "Puzzles") { classes(DashboardStyles.sidebarItemActive); attr("aria-current", "true") }
                        onClick {
                            viewModel.loadPuzzles()
                            route = DashboardRoute.Puzzles
                            closeNavigation()
                        }
                    }) { Text("Puzzles") }

                    Button({
                        classes(DashboardStyles.sidebarItem, "admin-nav-item")
                        if (route is DashboardRoute.Rewards) { classes(DashboardStyles.sidebarItemActive); attr("aria-current", "true") }
                        onClick {
                            viewModel.loadRewardsAdmin()
                            route = DashboardRoute.Rewards
                            closeNavigation()
                        }
                    }) { Text("Rewards") }

                    Button({
                        classes(DashboardStyles.sidebarItem, "admin-nav-item")
                        if (route is DashboardRoute.MobileLogs) { classes(DashboardStyles.sidebarItemActive); attr("aria-current", "true") }
                        onClick {
                            viewModel.loadMobileLogs()
                            route = DashboardRoute.MobileLogs
                            closeNavigation()
                        }
                    }) { Text("Mobile Logs") }
                }


                Div({
                    style { property("margin-top", "auto") }
                }) {
                    Button({
                        classes(DashboardStyles.secondaryButton)
                        style { width(100.percent) }
                        onClick { onLogout() }
                    }) {
                        Text("LOGOUT")
                    }
                }
            }
        }

        // Main Content Area
        Div({
            classes(DashboardStyles.contentArea, "admin-content")
            if (compactLayout && navigationOpen) attr("inert", "")
        }) {
            // Global Error Display
            if (uiState.error != null) {
                Div({ classes(DashboardStyles.errorBanner) }) {
                    Span { Text(uiState.error!!) }
                    Button({
                        style { backgroundColor(Color.transparent); border(0.px); color(Color("#b71c1c")); cursor("pointer"); fontSize(18.px); fontWeight("bold") }
                        onClick { viewModel.clearError() }
                    }) { Text("×") }
                }
            }

            when (val currentRoute = route) {
                is DashboardRoute.Communities -> {
                    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(20.px) } }) {
                        H2 { Text("Communities") }
                        if (isPlatformAdmin) {
                            Button({
                                classes(DashboardStyles.button)
                                onClick { showCreateCommunity = true }
                            }) { Text("CREATE COMMUNITY") }
                        }
                    }

                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 300px), 1fr))")
                            property("gap", "20px")
                        }
                    }) {
                        uiState.communities.forEach { community ->
                            Div({
                                classes(DashboardStyles.card)
                                style { cursor("pointer") }
                                onClick {
                                    viewModel.loadChallenges(community.id)
                                    viewModel.loadCommunityNews(community.id)
                                    route = DashboardRoute.CommunityDetail(community)
                                }
                            }) {
                                if (community.imageUrl.isNotEmpty()) {
                                    Img(src = community.imageUrl, attrs = {
                                        style {
                                            width(100.percent)
                                            height(150.px)
                                            property("object-fit", "cover")
                                            borderRadius(4.px)
                                            marginBottom(10.px)
                                        }
                                    })
                                }
                                H3({ style { margin(0.px, 0.px, 5.px, 0.px) } }) { Text(community.name) }
                                Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); color(Color("#666")); fontSize(14.px) } }) {
                                    Span { Text(community.area) }
                                    Span { Text("${community.membersCount} members") }
                                }
                            }
                        }
                    }
                }

                is DashboardRoute.Competitions -> {
                    CommunityCompetitionsAdminScreen(
                        uiState = uiState,
                        onRefresh = viewModel::loadCompetitions,
                        onCreate = { showCreateCompetition = true },
                        onEdit = { editingCompetition = it },
                        onOpen = { competition ->
                            viewModel.loadCompetition(competition.id)
                            route = DashboardRoute.CompetitionDetail(competition.id)
                        }
                    )
                }

                is DashboardRoute.CompetitionDetail -> {
                    val competition = uiState.selectedCompetition
                        ?.takeIf { it.id == currentRoute.competitionId }
                        ?: uiState.competitions.firstOrNull { it.id == currentRoute.competitionId }

                    if (competition == null) {
                        Div({ classes(DashboardStyles.card) }) {
                            P { Text(if (uiState.loading) "Loading competition..." else "Competition not found.") }
                        }
                    } else {
                        CommunityCompetitionDetailScreen(
                            competition = competition,
                            leaderboard = uiState.selectedCompetitionLeaderboard
                                ?.takeIf { it.competitionId == competition.id },
                            loading = uiState.loading,
                            onBack = { route = DashboardRoute.Competitions },
                            onEditDetails = { editingCompetition = competition },
                            onEditSchedule = { reschedulingCompetition = competition },
                            onCreateChallenge = { showCreateCompetitionChallenge = true },
                            onEditChallenge = { editingCompetitionChallenge = it },
                            onDeleteChallenge = { challengeId ->
                                viewModel.deleteCompetitionChallenge(competition.id, challengeId)
                            },
                            onActivate = { viewModel.activateCompetition(competition.id) },
                            onComplete = { viewModel.completeCompetition(competition.id) },
                            onEndEarly = { viewModel.endCompetitionEarly(competition.id) },
                            onDeleteDraft = {
                                viewModel.deleteCompetition(competition.id)
                                route = DashboardRoute.Competitions
                            }
                        )
                    }
                }

                is DashboardRoute.Questionnaires -> {
                    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(20.px) } }) {
                        H2 { Text("Questionnaires") }
                        Button({
                            classes(DashboardStyles.button)
                            onClick { showCreateQuestionnaire = true }
                        }) { Text("CREATE QUESTIONNAIRE") }
                    }

                    if (uiState.questionnaires.isEmpty()) {
                        P { Text("No questionnaires found.") }
                    } else {
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 300px), 1fr))")
                                property("gap", "20px")
                            }
                        }) {
                            uiState.questionnaires.forEach { questionnaire ->
                                Div({
                                    classes(DashboardStyles.card)
                                    style { cursor("pointer") }
                                    onClick {
                                        viewModel.loadQuestionnaire(questionnaire.id)
                                        route = DashboardRoute.QuestionnaireDetail(questionnaire.id)
                                    }
                                }) {
                                    H3({ style { margin(0.px, 0.px, 5.px, 0.px) } }) { Text(questionnaire.name) }
                                    if (!questionnaire.description.isNullOrEmpty()) {
                                        P({ style { color(Color("#666")); fontSize(14.px) } }) { Text(questionnaire.description) }
                                    }
                                    if (!questionnaire.infoUrl.isNullOrEmpty()) {
                                        A(href = questionnaire.infoUrl) { Text("Info Link") }
                                    }
                                }
                            }
                        }
                    }
                }

                is DashboardRoute.Puzzles -> {
                    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(20.px) } }) {
                        H2 { Text("Puzzles") }
                        Button({
                            classes(DashboardStyles.button)
                            onClick { showCreatePuzzle = true }
                        }) { Text("CREATE PUZZLE") }
                    }

                    if (uiState.puzzles.isEmpty()) {
                        P { Text("No puzzles found.") }
                    } else {
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 300px), 1fr))")
                                property("gap", "20px")
                            }
                        }) {
                            uiState.puzzles.forEach { puzzle ->
                                Div({
                                    classes(DashboardStyles.card)
                                    style { cursor("pointer") }
                                    onClick {
                                        viewModel.loadPuzzle(puzzle.id)
                                        route = DashboardRoute.PuzzleDetail(puzzle.id)
                                    }
                                }) {
                                    H3({ style { margin(0.px, 0.px, 5.px, 0.px) } }) { Text(puzzle.name) }
                                    if (!puzzle.description.isNullOrEmpty()) {
                                        P({ style { color(Color("#666")); fontSize(14.px) } }) { Text(puzzle.description) }
                                    }
                                }
                            }
                        }
                    }
                }

                is DashboardRoute.Rewards -> {
                    RewardsAdminScreen(
                        uiState = uiState,
                        onRefresh = viewModel::loadRewardsAdmin,
                        onCreateSponsor = { showCreateSponsor = true },
                        onEditSponsor = { editingSponsor = it },
                        onCreateReward = { showCreateReward = true },
                        onEditReward = { editingReward = it },
                        onValidateCode = viewModel::markRedemptionUsed
                    )
                }

                is DashboardRoute.MobileLogs -> {
                    val currentPage = uiState.mobileLogsPage
                    val totalPages = uiState.mobileLogsTotalPages
                    val hasPrevious = currentPage > 0
                    val hasNext = totalPages == 0 || currentPage + 1 < totalPages

                    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(20.px) } }) {
                        Div {
                            H2({ style { margin(0.px, 0.px, 6.px, 0.px) } }) { Text("Mobile Logs") }
                            P({ classes(DashboardStyles.mutedText); style { margin(0.px) } }) {
                                Text("Server-side logs sent from the Android and iOS apps.")
                            }
                        }
                        Button({
                            classes(DashboardStyles.secondaryButton)
                            onClick { viewModel.loadMobileLogs(currentPage, uiState.mobileLogsSize) }
                        }) { Text("REFRESH") }
                    }

                    Div({ classes(DashboardStyles.statsGrid) }) {
                        StatCard("Entries", uiState.mobileLogsTotalElements.toString())
                        StatCard("Current Page", (currentPage + 1).toString())
                        StatCard("Page Size", uiState.mobileLogsSize.toString())
                        StatCard("Total Pages", maxOf(totalPages, 1).toString())
                    }

                    if (uiState.mobileLogs.isEmpty()) {
                        Div({ classes(DashboardStyles.card) }) {
                            P { Text(if (uiState.loading) "Loading mobile logs..." else "No mobile logs found.") }
                        }
                    } else {
                        uiState.mobileLogs.forEach { log ->
                            Div({ classes(DashboardStyles.card) }) {
                                Div({ classes(DashboardStyles.logHeader, "admin-log-header") }) {
                                    Div {
                                        Div({ classes(DashboardStyles.logBadges) }) {
                                            LogBadge(log.type)
                                            log.platform?.takeIf { it.isNotBlank() }?.let { MetaBadge(it) }
                                            log.source?.takeIf { it.isNotBlank() }?.let { MetaBadge(it) }
                                        }
                                        Div({ classes(DashboardStyles.logMessage) }) {
                                            Text(log.message)
                                        }
                                    }

                                    Div({ classes(DashboardStyles.mutedText, "admin-log-date"); style { property("text-align", "right"); minWidth(180.px) } }) {
                                        P({ style { margin(0.px, 0.px, 6.px, 0.px) } }) { Text("Occurred") }
                                        P({ style { margin(0.px) } }) { Text(formatMobileLogTimestamp(log.occurredAt ?: log.createdAt)) }
                                    }
                                }

                                Div({ classes(DashboardStyles.logMetaGrid) }) {
                                    LogMetaItem("Created", formatMobileLogTimestamp(log.createdAt))
                                    LogMetaItem("Occurred", formatMobileLogTimestamp(log.occurredAt))
                                    LogMetaItem("App Version", buildVersionLabel(log.appVersion, log.buildNumber))
                                    LogMetaItem("OS", log.osVersion ?: "-")
                                    LogMetaItem("User", log.userId ?: "-")
                                    LogMetaItem("Session", log.sessionId ?: "-")
                                    LogMetaItem("Log ID", log.id)
                                }

                                if (!log.stackTrace.isNullOrBlank()) {
                                    H4({ style { margin(12.px, 0.px) } }) { Text("Stack Trace") }
                                    Pre({ classes(DashboardStyles.stackTrace) }) {
                                        Text(log.stackTrace)
                                    }
                                }
                            }
                        }
                    }

                    Div({ classes(DashboardStyles.paginationBar) }) {
                        Button({
                            classes(DashboardStyles.secondaryButton)
                            if (!hasPrevious) {
                                disabled()
                            }
                            onClick {
                                if (hasPrevious) {
                                    viewModel.loadMobileLogs(currentPage - 1, uiState.mobileLogsSize)
                                }
                            }
                        }) { Text("PREVIOUS") }

                        P({ classes(DashboardStyles.mutedText); style { margin(0.px) } }) {
                            Text("Page ${currentPage + 1} of ${maxOf(totalPages, 1)}")
                        }

                        Button({
                            classes(DashboardStyles.secondaryButton)
                            if (!hasNext || uiState.mobileLogs.isEmpty()) {
                                disabled()
                            }
                            onClick {
                                if (hasNext) {
                                    viewModel.loadMobileLogs(currentPage + 1, uiState.mobileLogsSize)
                                }
                            }
                        }) { Text("NEXT") }
                    }
                }

                is DashboardRoute.CommunityDetail -> {
                    val community = currentRoute.community
                    val communityNews = uiState.communityNews[community.id] ?: emptyList()
                    val commChallenges = uiState.challenges[community.id] ?: emptyList()
                    val administratorCandidates = uiState.communityAdministratorCandidates[community.id].orEmpty()
                    var administratorSearch by remember(community.id) { mutableStateOf("") }
                    val administratorSearchTerm = administratorSearch.trim()
                    val assignedAdministrators = administratorCandidates.filter { it.assigned }
                    val matchingAdministratorCandidates = if (administratorSearchTerm.length < 2) {
                        emptyList()
                    } else {
                        administratorCandidates.filter { candidate ->
                            !candidate.assigned && (
                                candidate.displayName.contains(administratorSearchTerm, ignoreCase = true) ||
                                    candidate.email.contains(administratorSearchTerm, ignoreCase = true)
                                )
                        }
                    }
                    val visibleAdministratorCandidates = matchingAdministratorCandidates.take(8)

                    LaunchedEffect(community.id) {
                        if (!uiState.communityNews.containsKey(community.id)) {
                            viewModel.loadCommunityNews(community.id)
                        }
                        if (isPlatformAdmin && !uiState.communityAdministratorCandidates.containsKey(community.id)) {
                            viewModel.loadCommunityAdministratorCandidates(community.id)
                        }
                    }

                    // Breadcrumbs
                    Div({ style { marginBottom(20.px); color(Color("#666")); fontSize(14.px) } }) {
                        Span({
                            style { cursor("pointer"); textDecoration("underline") }
                            onClick { route = DashboardRoute.Communities }
                        }) { Text("Communities") }
                        Text(" / ${community.name}")
                    }

                    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(20.px) } }) {
                        H2({ style { margin(0.px) } }) { Text(community.name) }
                        if (isPlatformAdmin) Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); gap(12.px) } }) {
                            Button({
                                classes(DashboardStyles.deleteButton)
                                style { padding(10.px, 24.px); fontSize(14.px) }
                                onClick {
                                    if (window.confirm("Are you sure you want to delete this community?")) {
                                        viewModel.deleteCommunity(community.id)
                                        route = DashboardRoute.Communities
                                    }
                                }
                            }) { Text("DELETE") }
                            Button({
                                classes(DashboardStyles.secondaryButton)
                                onClick { showEditCommunity = true }
                            }) { Text("EDIT") }
                        }
                    }

                    Div({ classes(DashboardStyles.card) }) {
                        Div({ classes("admin-detail-row"); style { display(DisplayStyle.Flex); gap(20.px) } }) {
                            if (community.imageUrl.isNotEmpty()) {
                                Img(src = community.imageUrl, attrs = { style { width(300.px); maxWidth(100.percent); height(200.px); property("object-fit", "cover"); borderRadius(8.px) } })
                            }
                            Div {
                                P { B { Text("Area: ") }; Text(community.area) }
                                P { B { Text("Description: ") }; Text(community.description) }
                                P { B { Text("Location: ") }; Text("Lat: ${community.latitude}, Lng: ${community.longitude}") }
                                P { B { Text("Members: ") }; Text("${community.membersCount}") }
                            }
                        }
                    }

                    CommunityEnergyAdminPanel(communityId = community.id, platformAdmin = isPlatformAdmin)

                    if (isPlatformAdmin) Div({ classes(DashboardStyles.card) }) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.SpaceBetween)
                                alignItems(AlignItems.Center)
                                gap(12.px)
                            }
                        }) {
                            H3({ style { margin(0.px) } }) { Text("Community Administrators") }
                            Span({ classes(DashboardStyles.badge); style { backgroundColor(Color("#ede7f6")); color(DashboardStyles.primaryColor) } }) {
                                Text("${assignedAdministrators.size} ASSIGNED")
                            }
                        }
                        P({ classes(DashboardStyles.mutedText) }) {
                            Text("Assign a community member to manage this community. The Community Admin role is synchronized with Keycloak automatically and takes effect on their next sign-in.")
                        }
                        if (administratorCandidates.isEmpty()) {
                            P { Text("No community members are available for assignment.") }
                        } else {
                            H4({ style { margin(20.px, 0.px, 4.px, 0.px) } }) {
                                Text("Assigned admins")
                            }
                            if (assignedAdministrators.isEmpty()) {
                                P({ classes(DashboardStyles.mutedText); style { marginTop(8.px) } }) {
                                    Text("No Community Admin has been assigned yet.")
                                }
                            } else {
                                assignedAdministrators.forEach { candidate ->
                                    CommunityAdministratorRow(
                                        candidate = candidate,
                                        onRemove = {
                                            if (window.confirm("Remove ${candidate.displayName} as Community Admin for ${community.name}?")) {
                                                viewModel.removeCommunityAdministrator(community.id, candidate.userId)
                                            }
                                        }
                                    )
                                }
                            }

                            Div({ style { marginTop(24.px) } }) {
                                FormLabel("Find a community member")
                                Input(InputType.Text) {
                                    classes(DashboardStyles.textInput)
                                    value(administratorSearch)
                                    placeholder("Search by name or email…")
                                    attr("autocomplete", "off")
                                    style { margin(0.px) }
                                    onInput { administratorSearch = it.value }
                                }
                            }

                            when {
                                administratorSearchTerm.length < 2 -> {
                                    P({ classes(DashboardStyles.mutedText); style { marginBottom(0.px) } }) {
                                        Text("Type at least 2 characters to search ${administratorCandidates.count { !it.assigned }} available members.")
                                    }
                                }

                                matchingAdministratorCandidates.isEmpty() -> {
                                    P({ classes(DashboardStyles.mutedText); style { marginBottom(0.px) } }) {
                                        Text("No available member matches “$administratorSearchTerm”.")
                                    }
                                }

                                else -> {
                                    P({ classes(DashboardStyles.mutedText); style { marginBottom(4.px) } }) {
                                        val resultLabel = if (matchingAdministratorCandidates.size > visibleAdministratorCandidates.size) {
                                            "Showing the first ${visibleAdministratorCandidates.size} of ${matchingAdministratorCandidates.size} matches"
                                        } else {
                                            "${matchingAdministratorCandidates.size} match${if (matchingAdministratorCandidates.size == 1) "" else "es"}"
                                        }
                                        Text(resultLabel)
                                    }
                                    visibleAdministratorCandidates.forEach { candidate ->
                                        CommunityAdministratorRow(
                                            candidate = candidate,
                                            onAssign = {
                                                viewModel.assignCommunityAdministrator(community.id, candidate.userId)
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }

                    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginTop(30.px); marginBottom(20.px) } }) {
                        H3({ style { margin(0.px) } }) { Text("Challenges") }
                        Button({
                            classes(DashboardStyles.button)
                            onClick { showCreateChallenge = true }
                        }) { Text("ADD CHALLENGE") }
                    }

                    if (commChallenges.isEmpty()) {
                        P { Text("No challenges yet.") }
                    } else {
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 280px), 1fr))")
                                property("gap", "20px")
                            }
                        }) {
                            commChallenges.forEach { challenge ->
                                val typeLabel = challenge.type
                                    .lowercase()
                                    .replace('_', ' ')
                                    .replaceFirstChar { it.uppercase() }
                                val difficultyLabel = challenge.difficulty
                                    .lowercase()
                                    .replace('_', ' ')
                                    .replaceFirstChar { it.uppercase() }
                                Div({
                                    classes(DashboardStyles.card)
                                    style { cursor("pointer") }
                                    onClick {
                                        route = DashboardRoute.ChallengeDetail(challenge, community)
                                    }
                                }) {
                                    H4({ style { margin(0.px, 0.px, 10.px, 0.px) } }) { Text(challenge.name) }
                                    Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); fontSize(14.px); color(Color("#666")) } }) {
                                        Span { Text("$typeLabel • $difficultyLabel") }
                                        Span { Text("${challenge.points} pts") }
                                    }
                                    Div({ style { marginTop(10.px); fontSize(12.px); color(Color("#888")) } }) {
                                        Span { Text("Participants: ${challenge.participantsCount}") }
                                        Span({ style { marginLeft(10.px) } }) { Text("Completed: ${challenge.completedCount}") }
                                    }
                                }
                            }
                        }
                    }

                    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginTop(30.px); marginBottom(20.px) } }) {
                        H3({ style { margin(0.px) } }) { Text("News") }
                        Button({
                            classes(DashboardStyles.button)
                            onClick { showCreateNews = true }
                        }) { Text("ADD NEWS") }
                    }

                    if (communityNews.isEmpty()) {
                        P { Text("No news yet.") }
                    } else {
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
                                property("gap", "20px")
                            }
                        }) {
                            communityNews.forEach { news ->
                                val isExpanded = expandedNewsIds[news.id] == true
                                Div({ classes(DashboardStyles.card) }) {
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            justifyContent(JustifyContent.SpaceBetween)
                                            alignItems(AlignItems.Center)
                                            gap(8.px)
                                            marginBottom(8.px)
                                        }
                                    }) {
                                        H4({ style { margin(0.px) } }) { Text(news.title) }
                                        Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); gap(8.px) } }) {
                                            Button({
                                                classes(DashboardStyles.secondaryButton)
                                                style { padding(6.px, 12.px); fontSize(12.px) }
                                                onClick { editingNews = news }
                                            }) { Text("EDIT") }
                                            Button({
                                                classes(DashboardStyles.deleteButton)
                                                style { padding(6.px, 12.px) }
                                                onClick {
                                                    if (window.confirm("Are you sure you want to delete this news item?")) {
                                                        viewModel.deleteCommunityNews(community.id, news.id)
                                                    }
                                                }
                                            }) { Text("DELETE") }
                                        }
                                    }
                                    P({
                                        style {
                                            color(Color("#4b5563"))
                                            margin(0.px, 0.px, 12.px, 0.px)
                                            if (!isExpanded) {
                                                property("display", "-webkit-box")
                                                property("-webkit-line-clamp", "3")
                                                property("-webkit-box-orient", "vertical")
                                                overflow("hidden")
                                            }
                                        }
                                    }) { Text(news.text) }
                                    if (news.text.length > 180) {
                                        Button({
                                            classes(DashboardStyles.secondaryButton)
                                            style {
                                                padding(6.px, 12.px)
                                                fontSize(12.px)
                                                property("text-transform", "none")
                                                marginBottom(12.px)
                                            }
                                            onClick {
                                                expandedNewsIds[news.id] = !isExpanded
                                            }
                                        }) {
                                            Text(if (isExpanded) "Show less" else "Read more")
                                        }
                                    }
                                    if (!news.imageUrl.isNullOrBlank()) {
                                        Img(src = news.imageUrl, attrs = {
                                            style {
                                                width(100.percent)
                                                height(180.px)
                                                property("object-fit", "cover")
                                                borderRadius(8.px)
                                                marginBottom(12.px)
                                            }
                                        })
                                    }
                                    P({ classes(DashboardStyles.mutedText); style { margin(0.px, 0.px, 6.px, 0.px) } }) {
                                        Text("Author: ${news.authorName}")
                                    }
                                    val newsSurveys = news.surveys.ifEmpty { news.survey?.let { listOf(it) } ?: emptyList() }
                                    if (newsSurveys.isNotEmpty()) {
                                        val rawIndex = newsSurveyPageIndex[news.id] ?: 0
                                        val selectedIndex = rawIndex.coerceIn(0, newsSurveys.lastIndex)
                                        if (selectedIndex != rawIndex) {
                                            newsSurveyPageIndex[news.id] = selectedIndex
                                        }
                                        val selectedSurvey = newsSurveys[selectedIndex]
                                        Div({
                                            style {
                                                property("border", "1px solid #e5e7eb")
                                                borderRadius(8.px)
                                                padding(10.px)
                                                marginBottom(8.px)
                                            }
                                        }) {
                                            Div({
                                                style {
                                                    display(DisplayStyle.Flex)
                                                    justifyContent(JustifyContent.SpaceBetween)
                                                    alignItems(AlignItems.Center)
                                                    marginBottom(8.px)
                                                }
                                            }) {
                                                P({
                                                    style {
                                                        margin(0.px)
                                                        fontSize(12.px)
                                                        color(Color("#64748b"))
                                                        fontWeight("600")
                                                    }
                                                }) {
                                                    Text("Survey ${selectedIndex + 1}/${newsSurveys.size}")
                                                }
                                                if (newsSurveys.size > 1) {
                                                    Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); gap(6.px) } }) {
                                                        Button({
                                                            classes(DashboardStyles.secondaryButton)
                                                            style {
                                                                padding(4.px, 8.px)
                                                                fontSize(11.px)
                                                                property("min-width", "42px")
                                                            }
                                                            if (selectedIndex == 0) {
                                                                disabled()
                                                            }
                                                            onClick {
                                                                newsSurveyPageIndex[news.id] = (selectedIndex - 1).coerceAtLeast(0)
                                                            }
                                                        }) { Text("Prev") }
                                                        Button({
                                                            classes(DashboardStyles.secondaryButton)
                                                            style {
                                                                padding(4.px, 8.px)
                                                                fontSize(11.px)
                                                                property("min-width", "42px")
                                                            }
                                                            if (selectedIndex == newsSurveys.lastIndex) {
                                                                disabled()
                                                            }
                                                            onClick {
                                                                newsSurveyPageIndex[news.id] = (selectedIndex + 1).coerceAtMost(newsSurveys.lastIndex)
                                                            }
                                                        }) { Text("Next") }
                                                    }
                                                }
                                            }
                                            P({ style { margin(0.px, 0.px, 8.px, 0.px); fontWeight("600"); color(Color("#1f2937")) } }) {
                                                Text(selectedSurvey.question)
                                            }
                                            selectedSurvey.options.forEach { option ->
                                                P({ classes(DashboardStyles.mutedText); style { margin(0.px, 0.px, 4.px, 0.px) } }) {
                                                    Text("• ${option.text} (${option.votes})")
                                                }
                                            }
                                            P({ classes(DashboardStyles.mutedText); style { margin(6.px, 0.px, 0.px, 0.px) } }) {
                                                Text("Responses: ${selectedSurvey.totalResponses}")
                                            }
                                        }
                                    }
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            gap(16.px)
                                            marginTop(10.px)
                                            marginBottom(8.px)
                                            fontSize(13.px)
                                            color(Color("#334155"))
                                        }
                                    }) {
                                        Span { Text("👍 ${news.likes}") }
                                        Span { Text("👎 ${news.dislikes}") }
                                        Span { Text("💬 ${news.comments.size}") }
                                    }
                                    if (news.comments.isNotEmpty()) {
                                        Div({
                                            style {
                                                property("border-top", "1px solid #e5e7eb")
                                                paddingTop(8.px)
                                                marginBottom(8.px)
                                            }
                                        }) {
                                            news.comments.take(3).forEach { comment ->
                                                P({
                                                    style {
                                                        margin(0.px, 0.px, 6.px, 0.px)
                                                        fontSize(13.px)
                                                        color(Color("#475569"))
                                                    }
                                                }) {
                                                    Text("${comment.authorName}: ${comment.text}")
                                                }
                                            }
                                            if (news.comments.size > 3) {
                                                P({
                                                    classes(DashboardStyles.mutedText)
                                                    style { margin(0.px) }
                                                }) {
                                                    Text("+${news.comments.size - 3} more comments")
                                                }
                                            }
                                        }
                                    }
                                    P({ classes(DashboardStyles.mutedText); style { margin(0.px) } }) {
                                        Text("Created: ${formatTimestamp(news.createdAt)}")
                                    }
                                }
                            }
                        }
                    }
                }

                is DashboardRoute.ChallengeDetail -> {
                    val challenge = currentRoute.challenge
                    val community = currentRoute.community
                    // Breadcrumbs
                    Div({ style { marginBottom(20.px); color(Color("#666")); fontSize(14.px) } }) {
                        Span({
                            style { cursor("pointer"); textDecoration("underline") }
                            onClick { route = DashboardRoute.Communities }
                        }) { Text("Communities") }
                        Text(" / ")
                        Span({
                            style { cursor("pointer"); textDecoration("underline") }
                            onClick { route = DashboardRoute.CommunityDetail(community) }
                        }) { Text(community.name) }
                        Text(" / ${challenge.name}")
                    }

                    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(20.px) } }) {
                        H2({ style { margin(0.px) } }) { Text("Challenge: ${challenge.name}") }
                        Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); gap(12.px) } }) {
                            Button({
                                classes(DashboardStyles.deleteButton)
                                style { padding(10.px, 24.px); fontSize(14.px) }
                                onClick {
                                    if (window.confirm("Are you sure you want to delete this challenge?")) {
                                        viewModel.deleteChallenge(community.id, challenge.id)
                                        route = DashboardRoute.CommunityDetail(community)
                                    }
                                }
                            }) { Text("DELETE") }
                            Button({
                                classes(DashboardStyles.secondaryButton)
                                onClick { showEditChallenge = true }
                            }) { Text("EDIT") }
                        }
                    }

                    Div({ classes(DashboardStyles.card) }) {
                        if (challenge.imageUrl.isNotEmpty()) {
                            Img(src = challenge.imageUrl, attrs = { style { maxWidth(100.percent); maxHeight(300.px); property("object-fit", "cover"); marginBottom(10.px); borderRadius(8.px) } })
                        }
                        P { B { Text("Description: ") }; Text(challenge.description) }
                        P { B { Text("Type: ") }; Text(challenge.type) }
                        if (challenge.type == ChallengeType.SAVINGS.name) {
                            challenge.savingsConfig?.let {
                                P { B { Text("Target reduction: ") }; Text("${it.targetReductionPercent}%") }
                            }
                        }
                        if (challenge.type == ChallengeType.SOCIAL_POST.name) {
                            challenge.socialConfig?.let {
                                P { B { Text("Platform: ") }; Text(it.platform) }
                                P { B { Text("Required tag: ") }; Text("#${it.requiredTag}") }
                                P { B { Text("Minimum likes/reposts: ") }; Text("${it.minLikes} / ${it.minReposts}") }
                            }
                        }
                        
                        val contentName = when {
                            challenge.type == ChallengeType.SAVINGS.name -> "Savings Settings"
                            challenge.type == ChallengeType.SOCIAL_POST.name -> "Social Post Settings"
                            challenge.contentId != null -> "View Content"
                            else -> "None"
                        }

                        P { 
                            B { Text("Linked Content: ") }
                            if (challenge.type == ChallengeType.SAVINGS.name) {
                                challenge.savingsConfig?.let {
                                    Text("Target reduction ${it.targetReductionPercent}%")
                                } ?: Text("None")
                            } else if (challenge.type == ChallengeType.SOCIAL_POST.name) {
                                challenge.socialConfig?.let {
                                    Text("${it.platform} post with #${it.requiredTag}")
                                } ?: Text("None")
                            } else if (challenge.contentId != null) {
                                Span({
                                    style { color(DashboardStyles.primaryColor); cursor("pointer"); textDecoration("underline") }
                                    onClick {
                                        if (challenge.type == "QUESTIONNAIRE") {
                                            viewModel.loadQuestionnaire(challenge.contentId)
                                            route = DashboardRoute.QuestionnaireDetail(challenge.contentId)
                                        } else {
                                            viewModel.loadPuzzle(challenge.contentId)
                                            route = DashboardRoute.PuzzleDetail(challenge.contentId)
                                        }
                                    }
                                }) { Text(contentName) }
                            } else {
                                Text("None")
                            }
                        }

                        P { B { Text("Difficulty: ") }; Text(challenge.difficulty) }
                        P { B { Text("Points: ") }; Text("${challenge.points}") }
                        P { B { Text("Duration: ") }; Text("${challenge.durationMinutes} mins") }
                        P { B { Text("Deadline: ") }; Text(challenge.deadline) }

                        Div({ style { margin(20.px, 0.px); property("border-top", "1px solid #E0E0E0") } })
                        H3 { Text("Statistics") }
                        Div({ style { display(DisplayStyle.Flex); gap(40.px) } }) {
                            Div {
                                P({ style { fontSize(24.px); fontWeight("bold"); margin(0.px) } }) { Text("${challenge.participantsCount}") }
                                P({ style { color(Color("#666")); margin(0.px) } }) { Text("Participants") }
                            }
                            Div {
                                P({ style { fontSize(24.px); fontWeight("bold"); margin(0.px) } }) { Text("${challenge.completedCount}") }
                                P({ style { color(Color("#666")); margin(0.px) } }) { Text("Completed") }
                            }
                        }
                    }
                }

                is DashboardRoute.QuizDetail -> {
                    val quiz = currentRoute.quiz
                    val challenge = currentRoute.challenge
                    val community = currentRoute.community

                    // Breadcrumbs
                    Div({ style { marginBottom(20.px); color(Color("#666")); fontSize(14.px) } }) {
                        Span({
                            style { cursor("pointer"); textDecoration("underline") }
                            onClick { route = DashboardRoute.Communities }
                        }) { Text("Communities") }
                        Text(" / ")
                        Span({
                            style { cursor("pointer"); textDecoration("underline") }
                            onClick { route = DashboardRoute.CommunityDetail(community) }
                        }) { Text(community.name) }
                        Text(" / ")
                        Span({
                            style { cursor("pointer"); textDecoration("underline") }
                            onClick { route = DashboardRoute.ChallengeDetail(challenge, community) }
                        }) { Text(challenge.name) }
                        Text(" / ${quiz.title}")
                    }

                    H2 { Text("Quiz: ${quiz.title}") }
                    P { Text(quiz.description) }

                    H3 { Text("Questions") }

                    if (quiz.questions.isEmpty()) {
                        P { Text("No questions in this quiz.") }
                    } else {
                        quiz.questions.forEachIndexed { i, q ->
                            Div({
                                classes(DashboardStyles.card)
                                style { marginBottom(15.px) }
                            }) {
                                H4 { Text("Q${i + 1}: ${q.text}") }
                                Ul({ style { listStyleType("none"); paddingLeft(0.px) } }) {
                                    q.options.forEach { opt ->
                                        val isCorrect = opt.id == q.correctOptionId
                                        Li({
                                            style {
                                                padding(8.px);
                                                borderRadius(4.px);
                                                marginBottom(4.px)
                                                if(isCorrect) backgroundColor(Color("#e8f5e9"))
                                            }
                                        }) {
                                            if (isCorrect) {
                                                Span({ style { color(Color("#2e7d32")); fontWeight("bold"); marginRight(8.px) } }) { Text("✓") }
                                            } else {
                                                Span({ style { marginRight(8.px) } }) { Text("○") }
                                            }
                                            Text(opt.text)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                is DashboardRoute.QuestionnaireDetail -> {
                    val qId = currentRoute.questionnaireId
                    val detail = uiState.selectedQuestionnaire
                    LaunchedEffect(qId) {
                        if (detail == null || detail.id != qId) {
                            viewModel.loadQuestionnaire(qId)
                        }
                    }

                    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(20.px) } }) {
                        H2 { Text(detail?.name ?: "Questionnaire") }
                        if (isPlatformAdmin) Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); gap(12.px) } }) {
                            Button({
                                classes(DashboardStyles.deleteButton)
                                style { padding(10.px, 24.px); fontSize(14.px) }
                                onClick {
                                    if (window.confirm("Are you sure you want to delete this questionnaire?")) {
                                        viewModel.deleteQuestionnaire(qId)
                                        route = DashboardRoute.Questionnaires
                                    }
                                }
                            }) { Text("DELETE") }
                            Button({
                                classes(DashboardStyles.secondaryButton)
                                onClick { showEditQuestionnaire = true }
                            }) { Text("EDIT") }
                            Button({ classes(DashboardStyles.button); onClick { showAddQuestion = true } }) { Text("ADD QUESTION") }
                        } else {
                            Span({
                                classes(DashboardStyles.badge)
                                style { backgroundColor(Color("#f3f4f6")); color(Color("#4b5563")) }
                            }) { Text("READ ONLY") }
                        }
                    }

                    if (detail == null) {
                        P { Text("Loading...") }
                    } else {
                        if (!detail.description.isNullOrEmpty()) {
                            P({ style { color(Color("#666")); marginBottom(10.px); fontSize(18.px) } }) { Text(detail.description) }
                        }
                        if (!detail.infoUrl.isNullOrEmpty()) {
                            P({ style { marginBottom(20.px) } }) {
                                A(href = detail.infoUrl) { Text("Info Link: ${detail.infoUrl}") }
                            }
                        }

                        if (detail.questions.isEmpty()) {
                            P { Text("No questions yet.") }
                        } else {
                            detail.questions.sortedBy { it.position }.forEachIndexed { idx, q ->
                                Div({ classes(DashboardStyles.card) }) {
                                    H4 { Text("Q${idx + 1}: ${q.text}") }
                                    Ul({ style { listStyleType("none"); paddingLeft(0.px) } }) {
                                        q.options.forEach { opt ->
                                            Li({ style { padding(6.px); marginBottom(4.px); borderRadius(4.px); backgroundColor(Color("#fafafa")) } }) {
                                                Text(opt.text)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    if (showAddQuestion && detail != null) {
                        AddQuestionDialog(
                            onDismiss = { showAddQuestion = false },
                            onCreate = { text, options ->
                                viewModel.addQuestionToQuestionnaire(detail.id, text, options)
                                showAddQuestion = false
                            }
                        )
                    }
                }

                is DashboardRoute.PuzzleDetail -> {
                    val pId = currentRoute.puzzleId
                    val detail = uiState.selectedPuzzle
                    LaunchedEffect(pId) {
                        if (detail == null || detail.id != pId) {
                            viewModel.loadPuzzle(pId)
                        }
                    }

                    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(20.px) } }) {
                        H2 { Text(detail?.name ?: "Puzzle") }
                        if (isPlatformAdmin) Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); gap(12.px) } }) {
                            Button({
                                classes(DashboardStyles.deleteButton)
                                style { padding(10.px, 24.px); fontSize(14.px) }
                                onClick {
                                    if (window.confirm("Are you sure you want to delete this puzzle?")) {
                                        viewModel.deletePuzzle(pId)
                                        route = DashboardRoute.Puzzles
                                    }
                                }
                            }) { Text("DELETE") }
                            Button({
                                classes(DashboardStyles.secondaryButton)
                                onClick { showEditPuzzle = true }
                            }) { Text("EDIT") }
                            Button({ classes(DashboardStyles.button); onClick { showAddPuzzleQuestion = true } }) { Text("ADD QUESTION") }
                        } else {
                            Span({
                                classes(DashboardStyles.badge)
                                style { backgroundColor(Color("#f3f4f6")); color(Color("#4b5563")) }
                            }) { Text("READ ONLY") }
                        }
                    }

                    if (detail == null) {
                        P { Text("Loading...") }
                    } else {
                        if (!detail.description.isNullOrEmpty()) {
                            P({ style { color(Color("#666")); marginBottom(20.px); fontSize(18.px) } }) { Text(detail.description) }
                        }

                        if (detail.questions == null || detail.questions.isEmpty()) {
                            P { Text("No questions yet.") }
                        } else {
                            detail.questions.forEachIndexed { idx, q ->
                                Div({ classes(DashboardStyles.card) }) {
                                    H4 { Text("Q${idx + 1}: ${q.text}") }
                                    Ul({ style { listStyleType("none"); paddingLeft(0.px) } }) {
                                        q.options.forEach { opt ->
                                            Li({ style { padding(6.px); marginBottom(4.px); borderRadius(4.px); backgroundColor(if (opt.isCorrect) Color("#e8f5e9") else Color("#fafafa")) } }) {
                                                Text("${opt.text} (${opt.points} pts)")
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    if (showAddPuzzleQuestion && detail != null) {
                        AddPuzzleQuestionDialog(
                            onDismiss = { showAddPuzzleQuestion = false },
                            onCreate = { text, explanation, options ->
                                val req = PuzzleQuestionCreateRequest(text, explanation, options)
                                viewModel.addPuzzleQuestion(detail.id, req)
                                showAddPuzzleQuestion = false
                            }
                        )
                    }
                }
            }
        }
    }
}

// --- Components ---

@Composable
fun CommunityDialog(
    title: String,
    confirmText: String,
    existingCommunity: Community? = null,
    onDismiss: () -> Unit,
    onConfirm: (Community) -> Unit
) {
    var name by remember { mutableStateOf(existingCommunity?.name ?: "") }
    var description by remember { mutableStateOf(existingCommunity?.description ?: "") }
    var imageUrl by remember { mutableStateOf(existingCommunity?.imageUrl ?: "") }
    var area by remember { mutableStateOf(existingCommunity?.area ?: "") }
    var lat by remember { mutableStateOf(existingCommunity?.latitude?.toString() ?: "0.0") }
    var lng by remember { mutableStateOf(existingCommunity?.longitude?.toString() ?: "0.0") }

    Modal(title = title, onDismiss = onDismiss, onConfirm = {
        onConfirm(
            Community(
                id = existingCommunity?.id ?: Random.nextInt().toString(),
                name = name,
                description = description,
                imageUrl = imageUrl,
                area = area,
                latitude = lat.toDoubleOrNull() ?: 0.0,
                longitude = lng.toDoubleOrNull() ?: 0.0,
                membersCount = existingCommunity?.membersCount ?: 0
            )
        )
    }, confirmText = confirmText) {
        FormInput("Name", name) { name = it }
        FormTextArea("Description", description) { description = it }
        FormInput("Image URL", imageUrl) { imageUrl = it }
        FormInput("Area", area) { area = it }
        Div({ classes(DashboardStyles.formGroup, "admin-form-row"); style { display(DisplayStyle.Flex); gap(16.px) } }) {
            Div({ style { flex(1) } }) {
                FormInput("Latitude", lat) { lat = it }
            }
            Div({ style { flex(1) } }) {
                FormInput("Longitude", lng) { lng = it }
            }
        }
    }
}

@Composable
fun ChallengeDialog(
    title: String,
    confirmText: String,
    existingChallengeDTO: ChallengeDTO? = null,
    uiState: DashboardUiState,
    viewModel: DashboardViewModel,
    useDateTimeDeadline: Boolean = false,
    minimumDeadline: String? = null,
    maximumDeadline: String? = null,
    defaultDeadline: String? = null,
    onDismiss: () -> Unit,
    onConfirm: (Challenge) -> Unit
) {
    val todayDate = remember { currentDateInputValue() }
    var name by remember { mutableStateOf(existingChallengeDTO?.name ?: "") }
    var description by remember { mutableStateOf(existingChallengeDTO?.description ?: "") }
    var imageUrl by remember { mutableStateOf(existingChallengeDTO?.imageUrl ?: "") }
    var points by remember { mutableStateOf(existingChallengeDTO?.points?.toString() ?: "10") }
    var duration by remember { mutableStateOf(existingChallengeDTO?.durationMinutes?.toString() ?: "60") }
    var deadline by remember {
        mutableStateOf(
            if (useDateTimeDeadline) {
                existingChallengeDTO?.deadline?.let(::toDateTimeInputValue)
                    ?: defaultDeadline
                    ?: ""
            } else {
                normalizeDateInputValue(existingChallengeDTO?.deadline) ?: todayDate
            }
        )
    }
    var difficulty by remember { mutableStateOf(
        existingChallengeDTO?.difficulty?.let { Challenge.Difficulty.valueOf(it) } ?: Challenge.Difficulty.EASY
    ) }
    var type by remember { mutableStateOf(
        existingChallengeDTO?.type?.let { ChallengeType.valueOf(it) } ?: ChallengeType.QUESTIONNAIRE
    ) }
    var contentId by remember { mutableStateOf(existingChallengeDTO?.contentId ?: "") }
    var targetReductionPercent by remember {
        mutableStateOf(existingChallengeDTO?.savingsConfig?.targetReductionPercent?.toString() ?: "10")
    }
    var socialPlatform by remember { mutableStateOf(existingChallengeDTO?.socialConfig?.platform ?: "TIKTOK") }
    var socialRequiredTag by remember { mutableStateOf(existingChallengeDTO?.socialConfig?.requiredTag ?: "") }
    var socialMinLikes by remember { mutableStateOf(existingChallengeDTO?.socialConfig?.minLikes?.toString() ?: "0") }
    var socialMinReposts by remember { mutableStateOf(existingChallengeDTO?.socialConfig?.minReposts?.toString() ?: "0") }

    LaunchedEffect(Unit) {
        viewModel.loadQuestionnaires()
        viewModel.loadPuzzles()
    }

    Modal(title = title, onDismiss = onDismiss, onConfirm = {
        onConfirm(
            Challenge(
                id = existingChallengeDTO?.id ?: Random.nextInt().toString(),
                name = name,
                description = description,
                imageUrl = imageUrl,
                points = points.toIntOrNull() ?: 0,
                durationMinutes = duration.toLongOrNull() ?: 0L,
                deadline = deadline,
                difficulty = difficulty,
                type = type,
                contentId = if (type == ChallengeType.SAVINGS || type == ChallengeType.SOCIAL_POST) null else contentId.ifBlank { null },
                savingsConfig = if (type == ChallengeType.SAVINGS) {
                    SavingsChallengeConfigDTO(
                        targetReductionPercent = targetReductionPercent.toIntOrNull() ?: 0
                    )
                } else {
                    null
                },
                socialConfig = if (type == ChallengeType.SOCIAL_POST) {
                    SocialChallengeConfigDTO(
                        platform = socialPlatform.trim().ifBlank { "TIKTOK" }.uppercase(),
                        requiredTag = socialRequiredTag.trim().removePrefix("#"),
                        minLikes = socialMinLikes.toIntOrNull() ?: 0,
                        minReposts = socialMinReposts.toIntOrNull() ?: 0
                    )
                } else {
                    null
                },
                isCompleted = false
            )
        )
    }, confirmText = confirmText) {
        FormInput("Name", name) { name = it }
        FormTextArea("Description", description) { description = it }
        FormInput("Image URL", imageUrl) { imageUrl = it }
        
        Div({ classes(DashboardStyles.formGroup, "admin-form-row"); style { display(DisplayStyle.Flex); gap(16.px) } }) {
            Div({ style { flex(1) } }) { FormInput("Points", points) { points = it } }
            Div({ style { flex(1) } }) { FormInput("Duration (minutes)", duration) { duration = it } }
        }
        
        FormInput(
            label = "Deadline",
            value = deadline,
            type = if (useDateTimeDeadline) InputType.DateTimeLocal else InputType.Date,
            minValue = if (useDateTimeDeadline) minimumDeadline else if (existingChallengeDTO == null) todayDate else null,
            maxValue = if (useDateTimeDeadline) maximumDeadline else null
        ) { deadline = it }

        Div({ classes(DashboardStyles.formGroup, "admin-form-row"); style { display(DisplayStyle.Flex); gap(16.px) } }) {
            Div({ style { flex(1) } }) {
                FormLabel("Difficulty")
                Select(attrs = {
                    classes(DashboardStyles.selectInput)
                    onInput { it.value?.let { v -> difficulty = Challenge.Difficulty.valueOf(v) } }
                }) {
                    for (diff in Challenge.Difficulty.values()) {
                        Option(diff.name, attrs = { if (diff == difficulty) selected() }) { Text(diff.name) }
                    }
                }
            }
            Div({ style { flex(1) } }) {
                FormLabel("Type")
                Select(attrs = {
                    classes(DashboardStyles.selectInput)
                    onInput { it.value?.let { v -> 
                        type = ChallengeType.valueOf(v) 
                        contentId = ""
                    } }
                }) {
                    for (t in ChallengeType.values()) {
                        Option(t.name, attrs = { if (t == type) selected() }) { Text(t.name) }
                    }
                }
            }
        }

        if (type == ChallengeType.SAVINGS) {
            FormInput("Target reduction (%)", targetReductionPercent) { targetReductionPercent = it }
        } else if (type == ChallengeType.SOCIAL_POST) {
            FormLabel("Platform")
            Select(attrs = {
                classes(DashboardStyles.selectInput)
                onInput { it.value?.let { v -> socialPlatform = v } }
            }) {
                listOf("TIKTOK", "YOUTUBE", "REDDIT").forEach { platform ->
                    Option(platform, attrs = { if (socialPlatform.equals(platform, ignoreCase = true)) selected() }) {
                        Text(platform)
                    }
                }
            }
            FormInput("Required tag (without #)", socialRequiredTag) { socialRequiredTag = it }
            Div({ classes(DashboardStyles.formGroup, "admin-form-row"); style { display(DisplayStyle.Flex); gap(16.px) } }) {
                Div({ style { flex(1) } }) { FormInput("Minimum likes", socialMinLikes) { socialMinLikes = it } }
                Div({ style { flex(1) } }) { FormInput("Minimum reposts", socialMinReposts) { socialMinReposts = it } }
            }
        } else {
            FormLabel(if (type == ChallengeType.QUESTIONNAIRE) "Select Questionnaire" else "Select Puzzle")
            if (uiState.adminContext?.platformAdmin != true) {
                P({ classes(DashboardStyles.mutedText); style { marginTop(0.px) } }) {
                    Text("Select content from the shared library. Content creation and editing is managed by the Admin.")
                }
            }
            Select(attrs = {
                classes(DashboardStyles.selectInput)
                onInput { it.value?.let { v -> contentId = v } }
            }) {
                Option("", attrs = { if (contentId.isEmpty()) selected() }) { Text("-- Select Content --") }
                if (type == ChallengeType.QUESTIONNAIRE) {
                    for (q in uiState.questionnaires) {
                        Option(q.id, attrs = { if (contentId == q.id) selected() }) { Text(q.name) }
                    }
                } else {
                    for (p in uiState.puzzles) {
                        Option(p.id, attrs = { if (contentId == p.id) selected() }) { Text(p.name) }
                    }
                }
            }
        }
    }
}

@Composable
fun CreateQuizDialog(challengeId: String, onDismiss: () -> Unit, onCreate: (Quiz) -> Unit) {
    var title by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }

    data class LocalOption(val text: String)
    data class LocalQuestion(var text: String, val options: MutableList<LocalOption>, var correctIndex: Int)

    val questions = remember { mutableStateListOf<LocalQuestion>() }

    Modal(title = "Create Quiz", onDismiss = onDismiss, onConfirm = {
        val quizQuestions = questions.mapIndexed { i, q ->
            val options = q.options.map { QuizOption(id = Random.nextInt().toString(), text = it.text) }
            val correctOptionId = if (options.isNotEmpty()) options[q.correctIndex].id else ""
            QuizQuestion(id = Random.nextInt().toString(), text = q.text, position = i, options = options, correctOptionId = correctOptionId)
        }
        onCreate(Quiz(id = Random.nextInt().toString(), challengeId = challengeId, title = title, description = description, questions = quizQuestions))
    }, confirmText = "Create Quiz") {
        FormInput("Quiz Title", title) { title = it }
        FormTextArea("Description", description) { description = it }

        H4({ style { marginBottom(12.px); marginTop(8.px) } }) { Text("Questions") }

        Div({
            style {
                maxHeight(300.px)
                overflowY("auto")
                border(1.px, LineStyle.Solid, DashboardStyles.dividerColor)
                borderRadius(8.px)
                padding(12.px)
                marginBottom(16.px)
            }
        }) {
            questions.forEachIndexed { qIndex, q ->
                Div({ style { marginBottom(20.px); paddingBottom(16.px); property("border-bottom", "1px solid #E0E0E0") } }) {
                    FormInput("Question ${qIndex + 1}", q.text) { q.text = it }

                    FormLabel("Options (Select correct one)")
                    q.options.forEachIndexed { oIndex, opt ->
                        Div({ classes("admin-option-row"); style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); marginBottom(8.px); gap(12.px) } }) {
                            Input(InputType.Radio) {
                                name("q_$qIndex")
                                checked(oIndex == q.correctIndex)
                                onChange { q.correctIndex = oIndex }
                            }
                            Input(InputType.Text) {
                                value(opt.text)
                                onInput { e -> q.options[oIndex] = opt.copy(text = e.value) }
                                classes(DashboardStyles.textInput)
                                style { margin(0.px); flex(1) }
                            }
                        }
                    }
                    Button({
                        classes(DashboardStyles.secondaryButton)
                        style { fontSize(12.px); padding(4.px, 12.px) }
                        onClick { q.options.add(LocalOption("New Option")) }
                    }) { Text("+ Add Option") }
                }
            }
        }

        Button({
            classes(DashboardStyles.secondaryButton)
            style { width(100.percent) }
            onClick { questions.add(LocalQuestion("", mutableListOf(LocalOption("Option 1"), LocalOption("Option 2")), 0)) }
        }) { Text("+ Add Question") }
    }
}

@Composable
fun CreateQuestionnaireDialog(
    initialName: String = "",
    initialDescription: String = "",
    initialInfoUrl: String = "",
    isEdit: Boolean = false,
    onDismiss: () -> Unit, 
    onCreate: (String, String?, String?) -> Unit
) {
    var name by remember { mutableStateOf(initialName) }
    var description by remember { mutableStateOf(initialDescription) }
    var infoUrl by remember { mutableStateOf(initialInfoUrl) }

    val title = if (isEdit) "Edit Questionnaire" else "Create Questionnaire"
    val buttonText = if (isEdit) "Update" else "Create"

    Modal(title = title, onDismiss = onDismiss, onConfirm = {
        onCreate(name, description.ifEmpty { null }, infoUrl.ifEmpty { null })
    }, confirmText = buttonText) {
        FormInput("Name", name) { name = it }
        FormTextArea("Description", description) { description = it }
        FormInput("Info URL", infoUrl) { infoUrl = it }
    }
}

@Composable
fun CreatePuzzleDialog(
    initialName: String = "",
    initialDescription: String = "",
    initialNumberOfQuestions: String = "10",
    isEdit: Boolean = false,
    onDismiss: () -> Unit, 
    onCreate: (String, String?, Int) -> Unit
) {
    var name by remember { mutableStateOf(initialName) }
    var description by remember { mutableStateOf(initialDescription) }
    var numberOfQuestions by remember { mutableStateOf(initialNumberOfQuestions) }

    val title = if (isEdit) "Edit Puzzle" else "Create Puzzle"
    val buttonText = if (isEdit) "Update" else "Create"

    Modal(title = title, onDismiss = onDismiss, onConfirm = {
        onCreate(name, description.ifEmpty { null }, numberOfQuestions.toIntOrNull() ?: 10)
    }, confirmText = buttonText) {
        FormInput("Name", name) { name = it }
        FormTextArea("Description", description) { description = it }
        FormInput("Number of Questions", numberOfQuestions) { numberOfQuestions = it }
    }
}

@Composable
fun CreateNewsDialog(
    initialTitle: String = "",
    initialText: String = "",
    initialImageUrl: String = "",
    initialSurveys: List<NewsSurveyDraft> = emptyList(),
    isEdit: Boolean = false,
    onDismiss: () -> Unit,
    onCreate: (String, String, String?, List<NewsSurveyCreateRequest>) -> Unit
) {
    var title by remember { mutableStateOf(initialTitle) }
    var text by remember { mutableStateOf(initialText) }
    var imageUrl by remember { mutableStateOf(initialImageUrl) }
    val surveyDrafts = remember {
        mutableStateListOf<NewsSurveyDraft>().apply {
            addAll(initialSurveys.ifEmpty { listOf(NewsSurveyDraft()) })
        }
    }

    val dialogTitle = if (isEdit) "Edit News" else "Create News"
    val buttonText = if (isEdit) "Update" else "Create"

    Modal(
        title = dialogTitle,
        onDismiss = onDismiss,
        onConfirm = {
            val surveys = surveyDrafts.mapNotNull { draft ->
                val question = draft.question.trim()
                val options = draft.optionsText
                    .lines()
                    .map { it.trim() }
                    .filter { it.isNotBlank() }
                if (question.isBlank() && options.isEmpty()) {
                    null
                } else {
                    NewsSurveyCreateRequest(question = question, options = options)
                }
            }
            onCreate(
                title.trim(),
                text.trim(),
                imageUrl.trim().ifBlank { null },
                surveys
            )
        },
        confirmText = buttonText
    ) {
        FormInput("Title", title) { title = it }
        FormTextArea("Text", text) { text = it }
        FormInput("Image URL (optional)", imageUrl) { imageUrl = it }

        FormLabel("Survey Questions (optional)")
        Div({ style { marginBottom(12.px) } }) {
            surveyDrafts.forEachIndexed { index, draft ->
                Div({
                    style {
                        property("border", "1px solid #e5e7eb")
                        borderRadius(8.px)
                        padding(10.px)
                        marginBottom(10.px)
                    }
                }) {
                    FormInput("Question ${index + 1}", draft.question) {
                        surveyDrafts[index] = draft.copy(question = it)
                    }
                    FormTextArea("Options (one per line)", draft.optionsText) {
                        surveyDrafts[index] = draft.copy(optionsText = it)
                    }
                    if (surveyDrafts.size > 1) {
                        Button({
                            classes(DashboardStyles.deleteButton)
                            style { padding(4.px, 10.px) }
                            onClick { surveyDrafts.removeAt(index) }
                        }) { Text("Remove Question") }
                    }
                }
            }
        }
        Button({
            classes(DashboardStyles.secondaryButton)
            style { width(100.percent) }
            onClick { surveyDrafts.add(NewsSurveyDraft()) }
        }) { Text("+ Add Survey Question") }
    }
}

data class NewsSurveyDraft(
    val question: String = "",
    val optionsText: String = ""
)

@Composable
fun AddQuestionDialog(onDismiss: () -> Unit, onCreate: (String, List<String>) -> Unit) {
    var text by remember { mutableStateOf("") }
    val options = remember { mutableStateListOf("", "") }

    Modal(title = "Add Question", onDismiss = onDismiss, onConfirm = {
        onCreate(text, options.filter { it.isNotBlank() })
    }, confirmText = "Create") {
        FormTextArea("Question Text", text) { text = it }

        FormLabel("Options")
        Div({ style { marginBottom(16.px) } }) {
            options.forEachIndexed { i, opt ->
                Div({ classes("admin-option-row"); style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px); marginBottom(8.px) } }) {
                    Input(InputType.Text) {
                        value(opt)
                        onInput { e -> options[i] = e.value }
                        classes(DashboardStyles.textInput)
                        style { margin(0.px); flex(1) }
                    }
                    Button({
                        classes(DashboardStyles.deleteButton)
                        onClick { options.removeAt(i) }
                    }) { Text("Remove") }
                }
            }
        }
        Button({
            classes(DashboardStyles.secondaryButton)
            style { width(100.percent) }
            onClick { options.add("") }
        }) { Text("+ Add Option") }
    }
}

@Composable
fun AddPuzzleQuestionDialog(onDismiss: () -> Unit, onCreate: (String, String?, List<PuzzleOptionCreateRequest>) -> Unit) {
    var text by remember { mutableStateOf("") }
    var explanation by remember { mutableStateOf("") }
    val options = remember { mutableStateListOf(PuzzleOptionCreateRequest("", 0, false)) }

    Modal(title = "Add Puzzle Question", onDismiss = onDismiss, onConfirm = {
        onCreate(text, explanation.ifEmpty { null }, options.filter { it.text.isNotBlank() })
    }, confirmText = "Create") {
        FormTextArea("Question Text", text) { text = it }
        FormTextArea("Explanation", explanation) { explanation = it }

        FormLabel("Options")
        Div({ style { marginBottom(16.px) } }) {
            options.forEachIndexed { i, opt ->
                Div({ classes("admin-option-row"); style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px); marginBottom(12.px) } }) {
                    Input(InputType.Text) {
                        value(opt.text)
                        onInput { e -> options[i] = options[i].copy(text = e.value) }
                        classes(DashboardStyles.textInput)
                        style { margin(0.px); flex(1) }
                    }
                    Input(InputType.Number) {
                        value(opt.points.toDouble())
                        onInput { e -> options[i] = options[i].copy(points = e.value?.toInt() ?: 0) }
                        classes(DashboardStyles.textInput)
                        style { margin(0.px); width(60.px) }
                    }
                    Input(InputType.Checkbox) {
                        checked(opt.isCorrect)
                        onInput { e -> options[i] = options[i].copy(isCorrect = e.value) }
                    }
                    Button({
                        classes(DashboardStyles.deleteButton)
                        onClick { options.removeAt(i) }
                    }) { Text("×") }
                }
            }
        }
        Button({
            classes(DashboardStyles.secondaryButton)
            style { width(100.percent) }
            onClick { options.add(PuzzleOptionCreateRequest("", 0, false)) }
        }) { Text("+ Add Option") }
    }
}

@Composable
fun CommunityCompetitionsAdminScreen(
    uiState: DashboardUiState,
    onRefresh: () -> Unit,
    onCreate: () -> Unit,
    onEdit: (CommunityCompetitionDTO) -> Unit,
    onOpen: (CommunityCompetitionDTO) -> Unit
) {
    Div({
        classes("admin-page-header")
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            marginBottom(20.px)
        }
    }) {
        Div {
            H2({ style { margin(0.px, 0.px, 6.px, 0.px) } }) { Text("Community Competitions") }
            P({ classes(DashboardStyles.mutedText); style { margin(0.px) } }) {
                Text("Create shared challenges where communities compete under the same rules.")
            }
        }
        Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); gap(10.px) } }) {
            Button({ classes(DashboardStyles.secondaryButton); onClick { onRefresh() } }) { Text("REFRESH") }
            Button({ classes(DashboardStyles.button); onClick { onCreate() } }) { Text("CREATE COMPETITION") }
        }
    }

    Div({ classes(DashboardStyles.statsGrid) }) {
        StatCard("Total", uiState.competitions.size.toString())
        StatCard("Draft", uiState.competitions.count { it.status == "DRAFT" }.toString())
        StatCard("Active", uiState.competitions.count { it.status == "ACTIVE" }.toString())
        StatCard("Completed", uiState.competitions.count { it.status == "COMPLETED" }.toString())
    }

    if (uiState.competitions.isEmpty()) {
        Div({ classes(DashboardStyles.card) }) {
            H3({ style { marginTop(0.px) } }) { Text("No competitions yet") }
            P({ classes(DashboardStyles.mutedText) }) {
                Text(if (uiState.loading) "Loading competitions..." else "Create the first draft and select its participating communities.")
            }
        }
    } else {
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 340px), 1fr))")
                gap(20.px)
            }
        }) {
            uiState.competitions.forEach { competition ->
                Div({
                    classes(DashboardStyles.card)
                    style {
                        property("border-top", "5px solid ${competition.themeColor ?: "#6200EE"}")
                        cursor("pointer")
                    }
                    onClick { onOpen(competition) }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.FlexStart)
                            gap(12.px)
                        }
                    }) {
                        Div {
                            H3({ style { margin(0.px, 0.px, 6.px, 0.px) } }) { Text(competition.name) }
                            Span({
                                classes(DashboardStyles.badge)
                                style {
                                    backgroundColor(Color(competitionStatusColor(competition.status)))
                                    color(Color.white)
                                }
                            }) { Text(competition.status) }
                        }
                        if (competition.status == "DRAFT") {
                            Button({
                                classes(DashboardStyles.secondaryButton)
                                style { padding(6.px, 12.px) }
                                onClick { event ->
                                    event.stopPropagation()
                                    onEdit(competition)
                                }
                            }) { Text("EDIT") }
                        }
                    }

                    if (competition.description.isNotBlank()) {
                        P({ style { color(Color("#4b5563")); property("line-height", "1.5") } }) {
                            Text(competition.description)
                        }
                    }

                    P({ classes(DashboardStyles.mutedText) }) {
                        Text("${formatCompetitionDate(competition.startsAt)} – ${formatCompetitionDate(competition.endsAt)}")
                    }
                    P {
                        B { Text("Communities: ") }
                        Text(
                            competition.communities.takeIf { it.isNotEmpty() }
                                ?.joinToString { it.name }
                                ?: "None selected"
                        )
                    }
                    P {
                        B { Text("Score weights: ") }
                        Text(
                            "Participation ${competition.participationWeight}% · " +
                                "Completion ${competition.completionWeight}% · " +
                                "Performance ${competition.performanceWeight}%"
                        )
                    }
                    P({ classes(DashboardStyles.mutedText); style { marginBottom(0.px) } }) {
                        Text("${competition.challenges.size} shared challenges · ${competition.eligibleMembersCount} eligible members")
                    }
                }
            }
        }
    }
}

@Composable
fun CommunityCompetitionDetailScreen(
    competition: CommunityCompetitionDTO,
    leaderboard: CommunityCompetitionLeaderboardDTO?,
    loading: Boolean,
    onBack: () -> Unit,
    onEditDetails: () -> Unit,
    onEditSchedule: () -> Unit,
    onCreateChallenge: () -> Unit,
    onEditChallenge: (ChallengeDTO) -> Unit,
    onDeleteChallenge: (String) -> Unit,
    onActivate: () -> Unit,
    onComplete: () -> Unit,
    onEndEarly: () -> Unit,
    onDeleteDraft: () -> Unit
) {
    val isDraft = competition.status == "DRAFT"
    val canActivate = isDraft && competition.communities.size >= 2 && competition.challenges.isNotEmpty()
    val hasStarted = parseBackendInstantOrNull(competition.startsAt)
        ?.let { Clock.System.now() >= it }
        ?: false
    val canFinalize = parseBackendInstantOrNull(competition.endsAt)
        ?.let { Clock.System.now() >= it }
        ?: false

    Div({ style { marginBottom(20.px); color(Color("#666")); fontSize(14.px) } }) {
        Span({
            style { cursor("pointer"); textDecoration("underline") }
            onClick { onBack() }
        }) { Text("Community Competitions") }
        Text(" / ${competition.name}")
    }

    Div({
        classes("admin-page-header")
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.FlexStart)
            gap(20.px)
            marginBottom(20.px)
        }
    }) {
        Div {
            Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px) } }) {
                H2({ style { margin(0.px) } }) { Text(competition.name) }
                Span({
                    classes(DashboardStyles.badge)
                    style {
                        backgroundColor(Color(competitionStatusColor(competition.status)))
                        color(Color.white)
                    }
                }) { Text(if (competition.endedEarlyAt != null) "COMPLETED EARLY" else competition.status) }
            }
            P({ classes(DashboardStyles.mutedText); style { marginBottom(0.px) } }) {
                Text("Community Competition · ${formatCompetitionDate(competition.startsAt)} – ${formatCompetitionDate(competition.endsAt)}")
            }
        }

        Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); flexWrap(FlexWrap.Wrap); gap(10.px); justifyContent(JustifyContent.FlexEnd) } }) {
            if (isDraft) {
                Button({
                    classes(DashboardStyles.deleteButton)
                    style { padding(10.px, 16.px); fontSize(13.px) }
                    onClick {
                        if (window.confirm("Delete this competition draft and all of its shared challenges?")) {
                            onDeleteDraft()
                        }
                    }
                }) { Text("DELETE DRAFT") }
                Button({
                    classes(DashboardStyles.secondaryButton)
                    onClick { onEditDetails() }
                }) { Text("EDIT DETAILS") }
                Button({
                    classes(DashboardStyles.button)
                    if (!canActivate || loading) {
                        disabled()
                        style { opacity(0.5); cursor("not-allowed") }
                    }
                    onClick {
                        if (window.confirm("Activate this competition? Communities, challenges and eligible members will be locked in.")) {
                            onActivate()
                        }
                    }
                }) { Text(if (loading) "WORKING..." else "ACTIVATE") }
            } else if (competition.status == "ACTIVE") {
                Button({
                    classes(DashboardStyles.secondaryButton)
                    if (!competition.canEditSchedule || loading) {
                        disabled()
                        attr("title", "Dates are locked after the first shared challenge is started, or once the competition expires.")
                    }
                    onClick { onEditSchedule() }
                }) { Text("EDIT DATES") }
                if (!canFinalize) {
                    Button({
                        classes(DashboardStyles.deleteButton)
                        style { padding(10.px, 16.px); fontSize(13.px) }
                        if (loading || !hasStarted) {
                            disabled()
                            style { opacity(0.5); cursor("not-allowed") }
                        }
                        onClick {
                            val confirmation = window.prompt(
                                "End '${competition.name}' early now? This immediately stops participation and permanently finalizes rankings, awards and badges. This cannot be undone. Type END to confirm."
                            )
                            if (confirmation?.trim() == "END") {
                                onEndEarly()
                            }
                        }
                    }) { Text(if (loading) "WORKING..." else "END COMPETITION EARLY") }
                }
                Button({
                    classes(DashboardStyles.button)
                    if (loading || !canFinalize) {
                        disabled()
                        style { opacity(0.5); cursor("not-allowed") }
                    }
                    onClick {
                        if (window.confirm("Finalize these results? This permanently locks rankings and awards and cannot be undone.")) {
                            onComplete()
                        }
                    }
                }) { Text(if (loading) "FINALIZING..." else "FINALIZE RESULTS") }
            }
        }
    }

    if (isDraft && !canActivate) {
        Div({
            classes(DashboardStyles.card)
            style {
                property("border-left", "4px solid #ef6c00")
                padding(16.px, 20.px)
            }
        }) {
            B { Text("Before activation: ") }
            Text(
                listOfNotNull(
                    if (competition.communities.size < 2) "select at least two communities" else null,
                    if (competition.challenges.isEmpty()) "create at least one shared challenge" else null
                ).joinToString(" and ")
            )
            Text(".")
        }
    }

    if (competition.status == "ACTIVE" && !canFinalize) {
        Div({
            classes(DashboardStyles.card)
            style {
                property("border-left", "4px solid #1565c0")
                padding(16.px, 20.px)
            }
        }) {
            B { Text("Results are still live. ") }
            Text("They can be finalized after ${formatCompetitionDate(competition.endsAt)}.")
        }
    }

    competition.endedEarlyAt?.let { endedEarlyAt ->
        Div({
            classes(DashboardStyles.card)
            style {
                property("border-left", "4px solid #b00020")
                padding(16.px, 20.px)
            }
        }) {
            B { Text("Competition ended early. ") }
            Text("Results were permanently finalized on ${formatCompetitionDate(endedEarlyAt)}.")
        }
    }

    Div({ classes(DashboardStyles.statsGrid) }) {
        StatCard("Communities", competition.communities.size.toString())
        StatCard("Shared Challenges", competition.challenges.size.toString())
        StatCard("Eligible Members", competition.eligibleMembersCount.toString())
        StatCard(
            "Score Weights",
            "${competition.participationWeight}/${competition.completionWeight}/${competition.performanceWeight}"
        )
    }

    if (!isDraft) {
        CompetitionLeaderboardAdminSection(leaderboard = leaderboard, loading = loading)
    }

    Div({ classes(DashboardStyles.card) }) {
        H3({ style { marginTop(0.px) } }) { Text("Competition setup") }
        if (competition.description.isNotBlank()) {
            P({ style { property("line-height", "1.5") } }) { Text(competition.description) }
        }
        P {
            B { Text("Period: ") }
            Text("${formatCompetitionDate(competition.startsAt)} – ${formatCompetitionDate(competition.endsAt)}")
        }
        P {
            B { Text("Scoring: ") }
            Text(
                "Participation ${competition.participationWeight}% · " +
                    "Completion ${competition.completionWeight}% · " +
                    "Performance ${competition.performanceWeight}%"
            )
        }
        P({ style { marginBottom(6.px) } }) { B { Text("Participating communities") } }
        if (competition.communities.isEmpty()) {
            P({ classes(DashboardStyles.mutedText); style { marginBottom(0.px) } }) { Text("No communities selected yet.") }
        } else {
            Div({ style { display(DisplayStyle.Flex); flexWrap(FlexWrap.Wrap); gap(8.px) } }) {
                competition.communities.forEach { community ->
                    Span({
                        classes(DashboardStyles.badge)
                        style { backgroundColor(Color("#ede7f6")); color(DashboardStyles.primaryColor) }
                    }) { Text("${community.name} · ${community.membersCount} members") }
                }
            }
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            marginTop(30.px)
            marginBottom(16.px)
        }
    }) {
        Div {
            H3({ style { margin(0.px, 0.px, 4.px, 0.px) } }) { Text("Shared Challenges") }
            P({ classes(DashboardStyles.mutedText); style { margin(0.px) } }) {
                Text("The same challenge applies to every participating community.")
            }
        }
        if (isDraft) {
            Button({ classes(DashboardStyles.button); onClick { onCreateChallenge() } }) {
                Text("ADD SHARED CHALLENGE")
            }
        }
    }

    if (competition.challenges.isEmpty()) {
        Div({ classes(DashboardStyles.card) }) {
            P({ classes(DashboardStyles.mutedText); style { margin(0.px) } }) {
                Text("No shared challenges yet.")
            }
        }
    } else {
        competition.challenges.forEach { challenge ->
            Div({
                classes(DashboardStyles.card)
                style { property("border-left", "4px solid ${competition.themeColor ?: "#6200EE"}") }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.FlexStart)
                        gap(16.px)
                    }
                }) {
                    Div {
                        H3({ style { margin(0.px, 0.px, 6.px, 0.px) } }) { Text(challenge.name) }
                        P({ classes(DashboardStyles.mutedText); style { margin(0.px) } }) {
                            Text("${challenge.type} · ${challenge.difficulty} · ${challenge.points} points · ${challenge.durationMinutes} min")
                        }
                    }
                    if (isDraft) {
                        Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); gap(8.px) } }) {
                            Button({
                                classes(DashboardStyles.secondaryButton)
                                style { padding(6.px, 12.px) }
                                onClick { onEditChallenge(challenge) }
                            }) { Text("EDIT") }
                            Button({
                                classes(DashboardStyles.deleteButton)
                                style { padding(6.px, 12.px) }
                                onClick {
                                    if (window.confirm("Delete shared challenge '${challenge.name}'?")) {
                                        onDeleteChallenge(challenge.id)
                                    }
                                }
                            }) { Text("DELETE") }
                        }
                    }
                }
                if (challenge.description.isNotBlank()) {
                    P({ style { color(Color("#4b5563")); property("line-height", "1.5") } }) { Text(challenge.description) }
                }
                P({ classes(DashboardStyles.mutedText); style { marginBottom(0.px) } }) {
                    Text("Deadline: ${formatCompetitionDate(challenge.deadline)}")
                }
            }
        }
    }
}

@Composable
private fun CompetitionLeaderboardAdminSection(
    leaderboard: CommunityCompetitionLeaderboardDTO?,
    loading: Boolean
) {
    Div({ classes(DashboardStyles.card) }) {
        H3({ style { marginTop(0.px); marginBottom(4.px) } }) {
            Text(if (leaderboard?.status == "COMPLETED") "Final Community Standings" else "Live Community Standings")
        }
        P({ classes(DashboardStyles.mutedText); style { marginTop(0.px) } }) {
            Text("Score combines participation, completion and normalized member performance.")
        }

        if (leaderboard == null) {
            P({ classes(DashboardStyles.mutedText); style { marginBottom(0.px) } }) {
                Text(if (loading) "Loading leaderboard..." else "Leaderboard is not available.")
            }
            return@Div
        }

        P({ classes("admin-table-hint") }) { Text("Swipe the table to see all columns.") }
        Div({
            classes("admin-table-scroll"); attr("role", "region")
            attr("aria-label", "Community standings"); attr("tabindex", "0")
            style {
                property("overflow-x", "auto")
                border(1.px, LineStyle.Solid, DashboardStyles.dividerColor)
                borderRadius(8.px)
            }
        }) {
            Table({
                style {
                    width(100.percent)
                    minWidth(920.px)
                    property("border-collapse", "collapse")
                }
            }) {
                Thead {
                    Tr({ style { backgroundColor(Color("#f5f3ff")) } }) {
                        CompetitionTableHeader("Rank", alignment = "center", minWidthPx = 64)
                        CompetitionTableHeader("Community", alignment = "left", minWidthPx = 220)
                        CompetitionTableHeader("Score", minWidthPx = 80)
                        CompetitionTableHeader("Participation", minWidthPx = 145)
                        CompetitionTableHeader("Completion", minWidthPx = 110)
                        CompetitionTableHeader("Performance", minWidthPx = 115)
                        CompetitionTableHeader("Points", minWidthPx = 80)
                        CompetitionTableHeader("Award", alignment = "left", minWidthPx = 150)
                    }
                }
                Tbody {
                    leaderboard.communities.forEach { score ->
                        Tr {
                            CompetitionTableCell("#${score.rank}", alignment = "center", emphasized = true)
                            CompetitionTableCell(score.community.name, alignment = "left", noWrap = false)
                            CompetitionTableCell(formatLeaderboardNumber(score.score), emphasized = true)
                            CompetitionTableCell("${formatLeaderboardNumber(score.participationRate)}% (${score.participants}/${score.eligibleMembers})")
                            CompetitionTableCell("${formatLeaderboardNumber(score.completionRate)}%")
                            CompetitionTableCell("${formatLeaderboardNumber(score.averagePerformance)}%")
                            CompetitionTableCell(score.totalPoints.toString())
                            CompetitionTableCell(score.award?.title ?: "Pending", alignment = "left", noWrap = false)
                        }
                    }
                }
            }
        }

        H4({ style { marginBottom(8.px) } }) { Text("Individual leaderboard") }
        if (leaderboard.individuals.isEmpty()) {
            P({ classes(DashboardStyles.mutedText); style { marginBottom(0.px) } }) { Text("No participation yet.") }
        } else {
            leaderboard.individuals.take(10).forEach { user ->
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        padding(8.px, 0.px)
                        property("border-bottom", "1px solid #eeeeee")
                    }
                }) {
                    Span { Text("#${user.rank} · ${user.displayName} · ${user.communityName}") }
                    B { Text("${user.totalPoints} pts") }
                }
            }
        }

        H4({ style { marginTop(28.px); marginBottom(4.px) } }) { Text("Standings by shared challenge") }
        P({ classes(DashboardStyles.mutedText); style { marginTop(0.px) } }) {
            Text("Each challenge ranks communities separately using the competition score weights. Awards follow the overall standings.")
        }
        leaderboard.challenges.forEach { challenge ->
            Div({ style { marginTop(22.px) } }) {
                H4({ style { marginBottom(10.px) } }) { Text(challenge.challengeName) }
                P({ classes("admin-table-hint") }) { Text("Swipe the table to see all columns.") }
                Div({
                    classes("admin-table-scroll"); attr("role", "region")
                    attr("aria-label", "Shared challenge community standings"); attr("tabindex", "0")
                    style {
                        property("overflow-x", "auto")
                        border(1.px, LineStyle.Solid, DashboardStyles.dividerColor)
                        borderRadius(8.px)
                    }
                }) {
                    Table({
                        style {
                            width(100.percent)
                            minWidth(760.px)
                            property("border-collapse", "collapse")
                        }
                    }) {
                        Thead {
                            Tr({ style { backgroundColor(Color("#f5f3ff")) } }) {
                                CompetitionTableHeader("Rank", alignment = "center", minWidthPx = 64)
                                CompetitionTableHeader("Community", alignment = "left", minWidthPx = 220)
                                CompetitionTableHeader("Score", minWidthPx = 80)
                                CompetitionTableHeader("Participation", minWidthPx = 145)
                                CompetitionTableHeader("Completion", minWidthPx = 110)
                                CompetitionTableHeader("Performance", minWidthPx = 115)
                                CompetitionTableHeader("Points", minWidthPx = 80)
                            }
                        }
                        Tbody {
                            challenge.communityStandings.forEach { score ->
                                Tr {
                                    CompetitionTableCell("#${score.rank}", alignment = "center", emphasized = true)
                                    CompetitionTableCell(score.community.name, alignment = "left", noWrap = false)
                                    CompetitionTableCell(formatLeaderboardNumber(score.score), emphasized = true)
                                    CompetitionTableCell("${formatLeaderboardNumber(score.participationRate)}% (${score.participants}/${score.eligibleMembers})")
                                    CompetitionTableCell("${formatLeaderboardNumber(score.completionRate)}% (${score.completedParticipations}/${score.possibleCompletions})")
                                    CompetitionTableCell("${formatLeaderboardNumber(score.averagePerformance)}%")
                                    CompetitionTableCell(score.totalPoints.toString())
                                }
                            }
                        }
                    }
                }
                H4({ style { marginBottom(8.px) } }) { Text("Individual standings") }
                if (challenge.entries.isEmpty()) {
                    P({ classes(DashboardStyles.mutedText); style { marginBottom(0.px) } }) { Text("No participation yet.") }
                } else {
                    challenge.entries.take(10).forEach { entry ->
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.SpaceBetween)
                                padding(8.px, 0.px)
                                property("border-bottom", "1px solid #eeeeee")
                            }
                        }) {
                            Span { Text("#${entry.rank} · ${entry.displayName} · ${entry.communityName}") }
                            B { Text("${entry.points} pts") }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CompetitionTableHeader(
    label: String,
    alignment: String = "right",
    minWidthPx: Int
) {
    Th({
        style {
            minWidth(minWidthPx.px)
            padding(12.px, 14.px)
            property("text-align", alignment)
            property("white-space", "nowrap")
            color(Color("#4b5563"))
            fontSize(12.px)
            fontWeight("700")
            property("text-transform", "uppercase")
            property("letter-spacing", "0.04em")
            property("border-bottom", "1px solid #e0e0e0")
        }
    }) { Text(label) }
}

@Composable
private fun CompetitionTableCell(
    value: String,
    alignment: String = "right",
    emphasized: Boolean = false,
    noWrap: Boolean = true
) {
    Td({
        style {
            padding(13.px, 14.px)
            property("text-align", alignment)
            if (noWrap) property("white-space", "nowrap")
            property("vertical-align", "middle")
            property("border-bottom", "1px solid #eeeeee")
            color(Color("#202124"))
            fontSize(14.px)
        }
    }) {
        if (emphasized) B { Text(value) } else Text(value)
    }
}

private fun formatLeaderboardNumber(value: Double): String =
    if (value % 1.0 == 0.0) value.toInt().toString() else value.toString()

@Composable
fun CompetitionScheduleDialog(
    competition: CommunityCompetitionDTO,
    onDismiss: () -> Unit,
    onConfirm: (CompetitionScheduleUpdateRequest) -> Unit
) {
    var startsAt by remember(competition.id) { mutableStateOf(toDateTimeInputValue(competition.startsAt)) }
    var endsAt by remember(competition.id) { mutableStateOf(toDateTimeInputValue(competition.endsAt)) }
    var description by remember(competition.id) { mutableStateOf(competition.description) }
    var formError by remember(competition.id) { mutableStateOf<String?>(null) }
    Modal(
        title = "Edit Competition Dates",
        confirmText = "Update Dates",
        onDismiss = onDismiss,
        onConfirm = {
            val start = runCatching { LocalDateTime.parse(startsAt).toInstant(TimeZone.currentSystemDefault()) }.getOrNull()
            val end = runCatching { LocalDateTime.parse(endsAt).toInstant(TimeZone.currentSystemDefault()) }.getOrNull()
            formError = when {
                start == null || end == null -> "Start and end date are required."
                end <= start -> "Competition end must be after its start."
                end <= Clock.System.now() -> "The new end date must be in the future."
                else -> null
            }
            if (formError == null && window.confirm(
                "Update competition dates? All ${competition.challenges.size} shared challenge deadlines will be set to the new end date. Communities, points and scoring rules will not change."
            )) {
                onConfirm(CompetitionScheduleUpdateRequest(start!!, end!!, description.trim()))
            }
        }
    ) {
        formError?.let { error -> Div({ classes(DashboardStyles.errorBanner) }) { Text(error) } }
        P { Text("Dates can only change before anyone starts a shared challenge. All shared challenge deadlines will match the new competition end date.") }
        FormInput("Starts at", startsAt, InputType.DateTimeLocal) { startsAt = it }
        FormInput("Ends at", endsAt, InputType.DateTimeLocal) { endsAt = it }
        FormTextArea("Description (update any dates mentioned in the text)", description) { description = it }
        P({ classes(DashboardStyles.mutedText) }) {
            Text("Times use your browser’s local time zone (${TimeZone.currentSystemDefault().id}). Communities, challenge content, points and score weights stay locked.")
        }
    }
}

@Composable
fun CommunityCompetitionDialog(
    communities: List<Community>,
    existingCompetition: CommunityCompetitionDTO? = null,
    onDismiss: () -> Unit,
    onConfirm: (CommunityCompetitionCreateRequest, List<String>) -> Unit
) {
    var name by remember(existingCompetition?.id) { mutableStateOf(existingCompetition?.name ?: "") }
    var description by remember(existingCompetition?.id) { mutableStateOf(existingCompetition?.description ?: "") }
    var imageUrl by remember(existingCompetition?.id) { mutableStateOf(existingCompetition?.imageUrl ?: "") }
    var themeColor by remember(existingCompetition?.id) { mutableStateOf(existingCompetition?.themeColor ?: "#6200EE") }
    var startsAt by remember(existingCompetition?.id) {
        mutableStateOf(toDateTimeInputValue(existingCompetition?.startsAt))
    }
    var endsAt by remember(existingCompetition?.id) {
        mutableStateOf(toDateTimeInputValue(existingCompetition?.endsAt))
    }
    var participationWeight by remember(existingCompetition?.id) {
        mutableStateOf((existingCompetition?.participationWeight ?: 40).toString())
    }
    var completionWeight by remember(existingCompetition?.id) {
        mutableStateOf((existingCompetition?.completionWeight ?: 30).toString())
    }
    var performanceWeight by remember(existingCompetition?.id) {
        mutableStateOf((existingCompetition?.performanceWeight ?: 30).toString())
    }
    val selectedCommunityIds = remember(existingCompetition?.id) {
        mutableStateListOf<String>().apply {
            addAll(existingCompetition?.communities?.map { it.id }.orEmpty())
        }
    }
    var formError by remember(existingCompetition?.id) { mutableStateOf<String?>(null) }
    val validThemeColor = Regex("^#[0-9A-Fa-f]{6}$")
    val pickerColor = themeColor.trim().takeIf(validThemeColor::matches) ?: "#6200EE"

    val isEdit = existingCompetition != null
    Modal(
        title = if (isEdit) "Edit Competition Draft" else "Create Community Competition",
        onDismiss = onDismiss,
        confirmText = if (isEdit) "Update Draft" else "Create Draft",
        onConfirm = {
            val participation = participationWeight.toIntOrNull()
            val completion = completionWeight.toIntOrNull()
            val performance = performanceWeight.toIntOrNull()
            val start = runCatching {
                LocalDateTime.parse(startsAt).toInstant(TimeZone.currentSystemDefault())
            }.getOrNull()
            val end = runCatching {
                LocalDateTime.parse(endsAt).toInstant(TimeZone.currentSystemDefault())
            }.getOrNull()

            formError = when {
                name.isBlank() -> "Competition name is required."
                start == null || end == null -> "Start and end date are required."
                end <= start -> "Competition end must be after its start."
                participation == null || completion == null || performance == null -> "All score weights are required."
                participation !in 0..100 || completion !in 0..100 || performance !in 0..100 -> "Each score weight must be between 0 and 100."
                participation + completion + performance != 100 -> "Score weights must total 100%."
                themeColor.isNotBlank() && !validThemeColor.matches(themeColor.trim()) -> "Theme color must be a 6-digit HEX value (for example, #0F766E)."
                else -> null
            }

            if (formError == null) {
                onConfirm(
                    CommunityCompetitionCreateRequest(
                        name = name.trim(),
                        description = description.trim(),
                        imageUrl = imageUrl.trim().ifBlank { null },
                        themeColor = themeColor.trim().ifBlank { null },
                        startsAt = start!!,
                        endsAt = end!!,
                        participationWeight = participation!!,
                        completionWeight = completion!!,
                        performanceWeight = performance!!
                    ),
                    selectedCommunityIds.toList()
                )
            }
        }
    ) {
        formError?.let { error ->
            Div({ classes(DashboardStyles.errorBanner) }) { Text(error) }
        }

        FormInput("Name", name) { name = it }
        FormTextArea("Description", description) { description = it }
        FormInput("Image URL (optional)", imageUrl) { imageUrl = it }
        Div({ classes(DashboardStyles.formGroup) }) {
            FormLabel("Theme color")
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
                Input(InputType.Color) {
                    value(pickerColor)
                    attr("aria-label", "Pick competition theme color")
                    style { width(52.px); height(44.px); padding(2.px); cursor("pointer") }
                    onInput { themeColor = it.value.uppercase() }
                }
                Input(InputType.Text) {
                    classes(DashboardStyles.textInput)
                    value(themeColor)
                    attr("placeholder", "#0F766E")
                    style { flex(1); margin(0.px) }
                    onInput { themeColor = it.value }
                }
            }
        }
        FormInput("Starts at", startsAt, InputType.DateTimeLocal) { startsAt = it }
        FormInput("Ends at", endsAt, InputType.DateTimeLocal) { endsAt = it }

        Div({ classes("admin-weight-fields") }) {
            FormInput("Participation %", participationWeight) { participationWeight = it }
            FormInput("Completion %", completionWeight) { completionWeight = it }
            FormInput("Performance %", performanceWeight) { performanceWeight = it }
        }

        FormLabel("Participating communities")
        P({ classes(DashboardStyles.mutedText); style { marginTop(0.px) } }) {
            Text("You can save an incomplete draft. At least two communities will be required before activation.")
        }
        Div({
            style {
                border(1.px, LineStyle.Solid, DashboardStyles.dividerColor)
                borderRadius(8.px)
                padding(12.px)
                maxHeight(220.px)
                overflowY("auto")
            }
        }) {
            if (communities.isEmpty()) {
                P({ classes(DashboardStyles.mutedText); style { margin(0.px) } }) { Text("No communities available.") }
            } else {
                communities.forEach { community ->
                    Label(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(10.px)
                            padding(8.px, 4.px)
                            cursor("pointer")
                        }
                    }) {
                        Input(InputType.Checkbox) {
                            checked(community.id in selectedCommunityIds)
                            onInput { event ->
                                if (event.value) {
                                    if (community.id !in selectedCommunityIds) selectedCommunityIds.add(community.id)
                                } else {
                                    selectedCommunityIds.remove(community.id)
                                }
                            }
                        }
                        Span { Text("${community.name} (${community.membersCount} members)") }
                    }
                }
            }
        }
    }
}

private fun competitionStatusColor(status: String): String = when (status) {
    "ACTIVE" -> "#2e7d32"
    "COMPLETED" -> "#455a64"
    else -> "#ef6c00"
}

private fun formatCompetitionDate(value: String): String {
    val instant = parseBackendInstantOrNull(value) ?: return value.replace('T', ' ').take(16)
    val local = instant.toLocalDateTime(TimeZone.currentSystemDefault())
    val month = local.monthNumber.toString().padStart(2, '0')
    val day = local.dayOfMonth.toString().padStart(2, '0')
    val hour = local.hour.toString().padStart(2, '0')
    val minute = local.minute.toString().padStart(2, '0')
    return "${local.year}-$month-$day $hour:$minute"
}

private fun toDateTimeInputValue(value: String?): String {
    val instant = parseBackendInstantOrNull(value) ?: return value
        ?.takeIf { it.isNotBlank() }
        ?.take(16)
        ?: ""
    val local = instant.toLocalDateTime(TimeZone.currentSystemDefault())
    val month = local.monthNumber.toString().padStart(2, '0')
    val day = local.dayOfMonth.toString().padStart(2, '0')
    val hour = local.hour.toString().padStart(2, '0')
    val minute = local.minute.toString().padStart(2, '0')
    return "${local.year}-$month-${day}T$hour:$minute"
}

// --- Helper UI Components ---

// Keep keyboard focus in temporary navigation/forms and return to the opener.
@Composable
private fun AdminFocusScope(elementId: String, onDismiss: () -> Unit) {
    val dismiss by rememberUpdatedState(onDismiss)
    DisposableEffect(elementId) {
        val previousFocus = document.activeElement as? HTMLElement
        val listener: (Event) -> Unit = { event ->
            val key = event as? KeyboardEvent
            if (key?.key == "Escape") {
                key.preventDefault()
                dismiss()
            } else if (key?.key == "Tab") {
                val nodes = document.getElementById(elementId)?.querySelectorAll(
                    "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex='0']"
                )
                val controls = (0 until (nodes?.length ?: 0)).mapNotNull { nodes?.item(it) as? HTMLElement }
                    .filter { it.getClientRects().isNotEmpty() }
                if (controls.isNotEmpty()) {
                    val target = when {
                        key.shiftKey && document.activeElement == controls.first() -> controls.last()
                        !key.shiftKey && document.activeElement == controls.last() -> controls.first()
                        else -> null
                    }
                    if (target != null) { key.preventDefault(); target.focus() }
                }
            }
        }
        window.addEventListener("keydown", listener)
        onDispose {
            window.removeEventListener("keydown", listener)
            previousFocus?.focus()
        }
    }
    LaunchedEffect(elementId) {
        (document.getElementById(elementId)?.querySelector("button") as? HTMLElement)?.focus()
    }
}

@Composable
fun Modal(
    title: String, 
    onDismiss: () -> Unit, 
    onConfirm: () -> Unit, 
    confirmText: String = "Confirm",
    content: @Composable () -> Unit
) {
    val dialogId = remember { "admin-dialog-${Random.nextInt(Int.MAX_VALUE)}" }
    AdminFocusScope(dialogId, onDismiss)
    Div({
        classes(DashboardStyles.dialogOverlay, "admin-dialog-overlay")
        onClick { onDismiss() }
    }) {
        Div({
            classes(DashboardStyles.dialogContent, "admin-dialog-content")
            id(dialogId)
            attr("role", "dialog"); attr("aria-modal", "true"); attr("aria-label", title)
            onClick { it.stopPropagation() }
        }) {
            // Header
            Div({ classes(DashboardStyles.dialogHeader, "admin-dialog-header") }) {
                H3({ classes(DashboardStyles.dialogTitle) }) { Text(title) }
                Button({
                    attr("aria-label", "Close dialog")
                    style { 
                        backgroundColor(Color.transparent); border(0.px); fontSize(24.px); cursor("pointer"); color(Color("#5f6368"))
                        lineHeight("1")
                    }
                    onClick { onDismiss() }
                }) { Text("×") }
            }
            
            // Body
            Div({ classes(DashboardStyles.dialogBody, "admin-dialog-body") }) {
                content()
            }
            
            // Footer
            Div({ classes(DashboardStyles.dialogFooter, "admin-dialog-footer") }) {
                Button({
                    classes(DashboardStyles.secondaryButton)
                    onClick { onDismiss() }
                }) { Text("Cancel") }
                Button({
                    classes(DashboardStyles.button)
                    onClick { onConfirm() }
                }) { Text(confirmText) }
            }
        }
    }
}

@Composable
fun CommunityAdministratorRow(
    candidate: CommunityAdministratorCandidateDTO,
    onAssign: (() -> Unit)? = null,
    onRemove: (() -> Unit)? = null
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            gap(16.px)
            padding(12.px, 0.px)
            property("border-bottom", "1px solid #eeeeee")
        }
    }) {
        Div {
            Div({ style { fontWeight("600") } }) { Text(candidate.displayName) }
            if (candidate.email.isNotBlank()) {
                Div({ classes(DashboardStyles.mutedText) }) { Text(candidate.email) }
            }
        }
        if (candidate.assigned) {
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) } }) {
                Span({
                    classes(DashboardStyles.badge)
                    style {
                        backgroundColor(Color("#ede7f6"))
                        color(DashboardStyles.primaryColor)
                    }
                }) { Text("COMMUNITY ADMIN") }
                if (onRemove != null) {
                    Button({
                        classes(DashboardStyles.deleteButton)
                        style { padding(7.px, 12.px); fontSize(12.px) }
                        onClick { onRemove() }
                    }) { Text("REMOVE") }
                }
            }
        } else if (onAssign != null) {
            Button({
                classes(DashboardStyles.secondaryButton)
                style { padding(7.px, 12.px); fontSize(12.px) }
                onClick { onAssign() }
            }) { Text("MAKE ADMIN") }
        }
    }
}

@Composable
fun FormLabel(text: String) {
    Label(attrs = { classes(DashboardStyles.formLabel) }) { Text(text) }
}

@Composable
fun FormInput(
    label: String,
    value: String,
    type: InputType<String> = InputType.Text,
    minValue: String? = null,
    maxValue: String? = null,
    onValueChange: (String) -> Unit
) {
    Div({ classes(DashboardStyles.formGroup) }) {
        FormLabel(label)
        Input(type) {
            classes(DashboardStyles.textInput)
            value(value)
            minValue?.let { attr("min", it) }
            maxValue?.let { attr("max", it) }
            style { margin(0.px) }
            onInput { onValueChange(it.value) }
        }
    }
}

@Composable
fun FormTextArea(label: String, value: String, onValueChange: (String) -> Unit) {
    Div({ classes(DashboardStyles.formGroup) }) {
        FormLabel(label)
        TextArea {
            classes(DashboardStyles.textInput)
            style { height(100.px); property("resize", "vertical"); fontFamily("inherit"); margin(0.px) }
            value(value)
            onInput { onValueChange(it.value) }
        }
    }
}

@Composable
fun RewardsAdminScreen(
    uiState: DashboardUiState,
    onRefresh: () -> Unit,
    onCreateSponsor: () -> Unit,
    onEditSponsor: (SponsorDTO) -> Unit,
    onCreateReward: () -> Unit,
    onEditReward: (RewardDTO) -> Unit,
    onValidateCode: (String) -> Unit
) {
    var redemptionCode by remember { mutableStateOf("") }

    Div({ classes("admin-page-header"); style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); marginBottom(20.px) } }) {
        Div {
            H2({ style { margin(0.px, 0.px, 6.px, 0.px) } }) { Text("Rewards") }
            P({ classes(DashboardStyles.mutedText); style { margin(0.px) } }) {
                Text("Manage sponsors, reward inventory and redemption codes.")
            }
        }
        Div({ classes("admin-actions"); style { display(DisplayStyle.Flex); gap(10.px) } }) {
            Button({ classes(DashboardStyles.secondaryButton); onClick { onRefresh() } }) { Text("REFRESH") }
            Button({ classes(DashboardStyles.button); onClick { onCreateSponsor() } }) { Text("ADD SPONSOR") }
            Button({
                classes(DashboardStyles.button)
                onClick { onCreateReward() }
                if (uiState.sponsors.isEmpty()) disabled()
            }) { Text("ADD REWARD") }
        }
    }

    Div({ classes(DashboardStyles.card); style { marginBottom(20.px) } }) {
        H3({ style { marginTop(0.px) } }) { Text("Validate redemption") }
        Div({ classes("admin-form-row"); style { display(DisplayStyle.Flex); gap(12.px); alignItems(AlignItems.End) } }) {
            Div({ style { flex(1) } }) {
                FormInput("Redemption code", redemptionCode) { redemptionCode = it.uppercase() }
            }
            Button({
                classes(DashboardStyles.button)
                style { marginBottom(16.px) }
                onClick { onValidateCode(redemptionCode.trim()) }
                if (redemptionCode.isBlank()) disabled()
            }) { Text("MARK USED") }
        }
        uiState.validatedRedemption?.let { redemption ->
            P({ style { color(Color("#2e7d32")); fontWeight("bold") } }) {
                Text("Redeemed ${redemption.rewardTitle} for ${redemption.sponsorName}. Code: ${redemption.code}")
            }
        }
    }

    Div({ classes(DashboardStyles.statsGrid); style { marginBottom(20.px) } }) {
        StatCard("Sponsors", uiState.sponsors.size.toString())
        StatCard("Rewards", uiState.rewards.size.toString())
        StatCard("Active rewards", uiState.rewards.count { it.active }.toString())
        StatCard("Available stock", uiState.rewards.sumOf { it.availableStock }.toString())
    }

    H3 { Text("Sponsors") }
    if (uiState.sponsors.isEmpty()) {
        Div({ classes(DashboardStyles.card) }) { P { Text("No sponsors yet.") } }
    } else {
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 280px), 1fr))")
                property("gap", "16px")
                marginBottom(24.px)
            }
        }) {
            uiState.sponsors.forEach { sponsor ->
                Div({ classes(DashboardStyles.card) }) {
                    Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Start); gap(8.px) } }) {
                        H4({ style { margin(0.px) } }) { Text(sponsor.name) }
                        Button({
                            classes(DashboardStyles.secondaryButton)
                            style { padding(6.px, 12.px); fontSize(12.px) }
                            onClick { onEditSponsor(sponsor) }
                        }) { Text("EDIT") }
                    }
                    P({ classes(DashboardStyles.mutedText) }) { Text(sponsor.description ?: "No description") }
                    P { B { Text("Status: ") }; Text(if (sponsor.active) "Active" else "Inactive") }
                    sponsor.contactEmail?.let { P { B { Text("Contact: ") }; Text(it) } }
                }
            }
        }
    }

    H3 { Text("Rewards") }
    if (uiState.rewards.isEmpty()) {
        Div({ classes(DashboardStyles.card) }) { P { Text("No rewards yet.") } }
    } else {
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
                property("gap", "16px")
            }
        }) {
            uiState.rewards.forEach { reward ->
                Div({ classes(DashboardStyles.card) }) {
                    Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Start); gap(8.px) } }) {
                        Div {
                            H4({ style { margin(0.px, 0.px, 4.px, 0.px) } }) { Text(reward.title) }
                            P({ classes(DashboardStyles.mutedText); style { margin(0.px) } }) { Text(reward.sponsorName) }
                        }
                        Button({
                            classes(DashboardStyles.secondaryButton)
                            style { padding(6.px, 12.px); fontSize(12.px) }
                            onClick { onEditReward(reward) }
                        }) { Text("EDIT") }
                    }
                    P { Text(reward.description) }
                    P { B { Text("Cost: ") }; Text("${reward.pointsCost} points") }
                    P { B { Text("Stock: ") }; Text("${reward.availableStock}/${reward.stock} available") }
                    P { B { Text("Type: ") }; Text(reward.redemptionType) }
                    P { B { Text("Status: ") }; Text(if (reward.active) "Active" else "Inactive") }
                }
            }
        }
    }
}

@Composable
fun SponsorDialog(
    title: String,
    confirmText: String,
    existingSponsor: SponsorDTO? = null,
    onDismiss: () -> Unit,
    onConfirm: (SponsorCreateRequest) -> Unit
) {
    var name by remember { mutableStateOf(existingSponsor?.name ?: "") }
    var description by remember { mutableStateOf(existingSponsor?.description ?: "") }
    var logoUrl by remember { mutableStateOf(existingSponsor?.logoUrl ?: "") }
    var websiteUrl by remember { mutableStateOf(existingSponsor?.websiteUrl ?: "") }
    var contactEmail by remember { mutableStateOf(existingSponsor?.contactEmail ?: "") }
    var active by remember { mutableStateOf(existingSponsor?.active ?: true) }

    Modal(title = title, onDismiss = onDismiss, onConfirm = {
        onConfirm(
            SponsorCreateRequest(
                name = name,
                description = description.ifBlank { null },
                logoUrl = logoUrl.ifBlank { null },
                websiteUrl = websiteUrl.ifBlank { null },
                contactEmail = contactEmail.ifBlank { null },
                active = active
            )
        )
    }, confirmText = confirmText) {
        FormInput("Name", name) { name = it }
        FormTextArea("Description", description) { description = it }
        FormInput("Logo URL", logoUrl) { logoUrl = it }
        FormInput("Website URL", websiteUrl) { websiteUrl = it }
        FormInput("Contact email", contactEmail) { contactEmail = it }
        ActiveSelect(active) { active = it }
    }
}

@Composable
fun RewardDialog(
    title: String,
    confirmText: String,
    sponsors: List<SponsorDTO>,
    existingReward: RewardDTO? = null,
    onDismiss: () -> Unit,
    onConfirm: (String, RewardCreateRequest) -> Unit
) {
    var sponsorId by remember { mutableStateOf(existingReward?.sponsorId ?: sponsors.firstOrNull()?.id.orEmpty()) }
    var rewardTitle by remember { mutableStateOf(existingReward?.title ?: "") }
    var description by remember { mutableStateOf(existingReward?.description ?: "") }
    var imageUrl by remember { mutableStateOf(existingReward?.imageUrl ?: "") }
    var pointsCost by remember { mutableStateOf(existingReward?.pointsCost?.toString() ?: "100") }
    var stock by remember { mutableStateOf(existingReward?.stock?.toString() ?: "10") }
    var validFrom by remember { mutableStateOf(existingReward?.validFrom?.take(16) ?: "") }
    var validUntil by remember { mutableStateOf(existingReward?.validUntil?.take(16) ?: "") }
    var terms by remember { mutableStateOf(existingReward?.terms ?: "") }
    var redemptionType by remember { mutableStateOf(existingReward?.redemptionType ?: "QR_CODE") }
    var active by remember { mutableStateOf(existingReward?.active ?: true) }

    Modal(title = title, onDismiss = onDismiss, onConfirm = {
        onConfirm(
            sponsorId,
            RewardCreateRequest(
                title = rewardTitle,
                description = description,
                imageUrl = imageUrl.ifBlank { null },
                pointsCost = pointsCost.toIntOrNull() ?: 0,
                stock = stock.toIntOrNull() ?: 0,
                validFrom = validFrom.ifBlank { null },
                validUntil = validUntil.ifBlank { null },
                terms = terms.ifBlank { null },
                redemptionType = redemptionType,
                active = active
            )
        )
    }, confirmText = confirmText) {
        if (existingReward == null) {
            FormLabel("Sponsor")
            Select(attrs = {
                classes(DashboardStyles.selectInput)
                onInput { it.value?.let { value -> sponsorId = value } }
            }) {
                sponsors.forEach { sponsor ->
                    Option(sponsor.id, attrs = { if (sponsorId == sponsor.id) selected() }) { Text(sponsor.name) }
                }
            }
        } else {
            P { B { Text("Sponsor: ") }; Text(existingReward.sponsorName) }
        }
        FormInput("Title", rewardTitle) { rewardTitle = it }
        FormTextArea("Description", description) { description = it }
        FormInput("Image URL", imageUrl) { imageUrl = it }
        Div({ classes(DashboardStyles.formGroup, "admin-form-row"); style { display(DisplayStyle.Flex); gap(16.px) } }) {
            Div({ style { flex(1) } }) { FormInput("Points cost", pointsCost) { pointsCost = it } }
            Div({ style { flex(1) } }) { FormInput("Stock", stock) { stock = it } }
        }
        Div({ classes(DashboardStyles.formGroup, "admin-form-row"); style { display(DisplayStyle.Flex); gap(16.px) } }) {
            Div({ style { flex(1) } }) { FormInput("Valid from", validFrom, InputType.DateTimeLocal) { validFrom = it } }
            Div({ style { flex(1) } }) { FormInput("Valid until", validUntil, InputType.DateTimeLocal) { validUntil = it } }
        }
        FormTextArea("Terms", terms) { terms = it }
        FormLabel("Redemption type")
        Select(attrs = {
            classes(DashboardStyles.selectInput)
            onInput { it.value?.let { value -> redemptionType = value } }
        }) {
            listOf("QR_CODE", "TEXT_CODE", "MANUAL").forEach { type ->
                Option(type, attrs = { if (redemptionType == type) selected() }) { Text(type) }
            }
        }
        ActiveSelect(active) { active = it }
    }
}

@Composable
private fun ActiveSelect(active: Boolean, onChange: (Boolean) -> Unit) {
    FormLabel("Status")
    Select(attrs = {
        classes(DashboardStyles.selectInput)
        onInput { it.value?.let { value -> onChange(value == "true") } }
    }) {
        Option("true", attrs = { if (active) selected() }) { Text("Active") }
        Option("false", attrs = { if (!active) selected() }) { Text("Inactive") }
    }
}

@Composable
private fun StatCard(label: String, value: String) {
    Div({ classes(DashboardStyles.statCard) }) {
        P({ classes(DashboardStyles.mutedText); style { margin(0.px, 0.px, 8.px, 0.px) } }) { Text(label) }
        H3({ style { margin(0.px) } }) { Text(value) }
    }
}

@Composable
private fun LogMetaItem(label: String, value: String) {
    Div {
        P({ classes(DashboardStyles.mutedText); style { margin(0.px, 0.px, 4.px, 0.px) } }) { Text(label) }
        P({ style { margin(0.px); property("word-break", "break-word") } }) { Text(value) }
    }
}

@Composable
private fun LogBadge(type: String) {
    val normalized = type.uppercase()
    val toneClass = when (normalized) {
        "ERROR", "FATAL" -> DashboardStyles.badgeError
        "WARN", "WARNING" -> DashboardStyles.badgeWarn
        "INFO" -> DashboardStyles.badgeInfo
        else -> DashboardStyles.badgeNeutral
    }

    Span({
        classes(DashboardStyles.badge)
        classes(toneClass)
    }) {
        Text(normalized)
    }
}

@Composable
private fun MetaBadge(value: String) {
    Span({
        classes(DashboardStyles.badge)
        classes(DashboardStyles.badgeNeutral)
    }) {
        Text(value)
    }
}

private fun formatMobileLogTimestamp(value: String?): String {
    if (value.isNullOrBlank()) return "-"
    return value
        .replace('T', ' ')
        .substringBefore('[')
        .substringBeforeLast(".")
}

private fun formatTimestamp(value: String?): String {
    if (value.isNullOrBlank()) return "-"
    return value
        .replace('T', ' ')
        .substringBefore('[')
        .substringBeforeLast(".")
}

private fun buildVersionLabel(appVersion: String?, buildNumber: String?): String = when {
    !appVersion.isNullOrBlank() && !buildNumber.isNullOrBlank() -> "$appVersion ($buildNumber)"
    !appVersion.isNullOrBlank() -> appVersion
    !buildNumber.isNullOrBlank() -> buildNumber
    else -> "-"
}

private fun normalizeDateInputValue(value: String?): String? {
    if (value.isNullOrBlank()) return null

    val normalized = value
        .substringBefore('T')
        .substringBefore(' ')
        .trim()

    return normalized.takeIf { it.matches(Regex("\\d{4}-\\d{2}-\\d{2}")) }
}

private fun currentDateInputValue(): String {
    val date = js("new Date()")
    val year = date.getFullYear().toString()
    val month = (date.getMonth() as Int + 1).toString().padStart(2, '0')
    val day = (date.getDate() as Int).toString().padStart(2, '0')
    return "$year-$month-$day"
}
